import { v4 as uuidv4 } from "uuid";
import { observer } from "mobx-react";
import { makeAutoObservable, reaction, runInAction } from "mobx";
import db from "../Firestore/FirestoreConfig";
import Block from "./Block";
import Workout from "./Workout";
import Exercise from "./Exercise";
import Serie from "./Serie";
import Attribute from "./Attribute";
import Competition from "./Competition";
import Note from "./Note";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  setDoc,
  limit,
  doc,
  writeBatch,
  updateDoc,
  deleteField,
  runTransaction,
  deleteDoc,
  onSnapshot,
  or,
  orderBy,
  serverTimestamp,
  arrayUnion,
  increment,
  getFirestore,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";

import Form from "./Form";
import Question from "./Question";
import FormSection from "./FormSection";
import Bodyweight from "./Bodyweight";
import Trac from "./Trac";

import {
  Chat,
  Calendar,
  MessageStatus,
  ProtocolTitles,
  SyncStatus,
  MealNames,
} from "../../Resources/Constants";
import { ClientSyncStatusManager } from "../Classes/ClientSyncStatusManager";
import ClientFirestoreListener from "./ClientFirestoreListener";
import NetworkManager from "../Classes/NetworkManager";
import {
  endOfWeek,
  startOfWeek,
  getMondayOfWeek,
  getSundayOfWeek,
} from "../utils/dateUtils";
import {
  resolveClientBlockDocPath,
  resolveClientBlocksCollectionPath,
  resolvePendingWorkspaceDocPath,
  shouldUsePendingBlockPath,
} from "../invitations/clientBlockPathResolver";
import {
  commitProgramBlockTransaction,
  commitProgramBlockWrite,
  deleteProgramBlock,
  getChangedWorkoutIdsFromBlock,
} from "./programBlockWriteService";
import {
  shouldRefreshPendingWorkspaceDiscoverabilityAfterMutation,
  shouldSkipUserScopedForms,
} from "../invitations/pendingClientDataAccess";
import {
  buildCoachCancelInvitationPatch,
  INVITATION_STATE,
  normalizeInvitationState,
} from "../invitations/invitationIdentity";
import {
  buildPendingWorkspaceStatePatch,
  isWorkablePendingWorkspaceClient,
  normalizePendingWorkspaceWeekWorkouts,
} from "../invitations/pendingWorkspaceState";
import ChatMessage from "./ChatMessage";
import ChatManager from "../Classes/ChatManager";
import ChatOutboxManager, {
  buildChatOutboxRecord,
  ChatOutboxErrorStage,
} from "../Classes/ChatOutboxManager";
import { shouldUploadChatMessageToBatch } from "../Chat/chatMessageUploadGuards";
import { buildClientCompetitionSnapshotUpdate } from "../CoachViews/CoachCompetitions/competitionWeightClassPersistence";
import {
  hasRejectedTransactionResults,
  propagateCompetitionTransactionError,
} from "../utils/competitionSyncGuards";
import ClientNutritionPlanStore from "./nutrition/ClientNutritionPlanStore";
import {
  selectRetainedBlocks,
  shouldRehydrateFullFetch,
} from "./clientMemoryWindowUtils";
import { toRgba } from "../utils/colorUtils";
import {
  normalizeAllergenCodes,
  normalizeUserDietaryPreference,
} from "../nutrition/foods/foodClassification";
import {
  deleteActiveClientNutritionData,
} from "./nutrition/clientNutritionCleanup";
import { syncUntouchedClientProfileDraftFields } from "../platform/guards/clientProfileUnsavedChanges";

const normalizeNutritionPlanTotalsOverride = (value) => {
  if (value === true) {
    return true;
  }

  if (value === false) {
    return false;
  }

  return null;
};

const buildDeleteClientProfileCleanupPayload = () => ({
  coach_id: deleteField(),
  coach_language: deleteField(),
  coach_exercise_unit: deleteField(),
  coach_rir_or_rpe: deleteField(),
  is_checked: deleteField(),
  phone: deleteField(),
  last_week_workouts: deleteField(),
  week_workouts: deleteField(),
  name_for_coach: deleteField(),
  surname_for_coach: deleteField(),
  block_weeks: deleteField(),
  block_current_week: deleteField(),
  fee_history: deleteField(),
  weekly_form_id: deleteField(),
  block_form_id: deleteField(),
  coach_name: deleteField(),
  coach_surname: deleteField(),
  coach_email: deleteField(),
  dietary_preference: deleteField(),
  allergen_codes: deleteField(),
  show_nutrition_plan_totals_override: deleteField(),
});

export class Client {
  /* CLIENT ARRAYS */
  clientTransactionData = [];
  clientDeletionData = [];
  clientDeletionWorkouts = [];
  clientTransactionNotes = [];
  clientDeletionNotes = [];
  clientTransactionCompetitions = [];
  clientDeletionCompetitions = [];
  clientTransactionForms = [];
  clientDeletionForms = [];
  clientTransactionProfile = [];

  /* CLIENT */
  isLoading = true;
  isFetched = false;
  hasBeenFetchedOnce = false;
  wasPartiallyEvicted = false;
  firestoreListener = new ClientFirestoreListener(this);
  syncStatusManager = new ClientSyncStatusManager(this);
  competitions = [];
  notesArray = [];
  forms = [];
  blocks = [];
  tracs = [];
  bodyweights = [];
  batch = null;
  batchTimer = null;
  avoidTransaction = false;
  isHistoryShowing = true;
  clientSettings = {};
  clientSettingsBaseline = {};
  selectedTab = "summary-string";
  selectedToRenew = false;
  pendingUserId = "";
  usePendingBlocksPath = false;
  invitationEmail = "";
  invitationCreatedAtMs = null;

  /* STATISTICS */
  selectedMetric = "e1rm-string";
  selectedMetrics = ["e1rm-string", "Stress Index", "tonnage-string"];
  startWeek = 1;
  endWeek = 1;
  exercisesMetricsPicked = [];
  metricsSelectedBlocks = [];
  exercisesMetricsSelected = [];
  metricsEndDate = new Date();
  metricsStartDate = new Date(
    new Date().setMonth(this.metricsEndDate.getMonth() - 3)
  );

  /* CALENDAR */
  weekCalendarYear = new Date().getFullYear();
  selectedMacro = "";
  focusedBlock = null;
  scrollRef = null;
  calendarScrollPosition = 0;
  showingBlocksWithoutDate = false;
  blockHoverStart = null;
  blockHoverSpan = 0;
  blockHoverId = null;
  showingMoveBlockWithWorkoutsWarning = false;
  initCalendarScroll = false;

  /* COMPETITIONS */
  selectedWeightClass = "";
  isSelectedComp = false;

  /* BLOCKS */
  shownBlocksInHistory = 2;
  showingInfoView = false; //Used for small screens

  /* CHATS */
  messages = [];
  isChatOpen = false;
  unreadCount = 0;
  unreadMark = false;
  chatText = "";
  messageIds = new Set();
  chatOutboxPendingCount = 0;
  chatOutboxFlushPromise = null;
  chatOutboxFlushRequested = false;

  /* NUTRITION */
  nutritionPlans = [];
  auxNutritionPlan = null;
  nutritionEditingPlanId = null;

  foodHistory = [];

  clientTransactionNutritionPlans = [];
  clientDeletionNutritionPlans = [];

  /* SUMMARY */
  summaryStartDate = null; // Monday of selected week
  blockCurrentWeekPrepared = true;
  fetchedBlockFormIds = []; // Track block IDs that have been attempted to fetch forms for
  fetchedWeeklyFormStartDates = []; // Track week start dates that have been attempted to fetch weekly forms for

  // Used to invalidate in-flight fetches when unloading this client from memory
  fetchSessionId = 0;

  constructor(
    id = uuidv4(),
    email = "",
    name = "",
    surname = "",
    nameForCoach = "",
    surnameForCoach = "",
    bodyweightUnit = false,
    exerciseUnit = false,
    lastBackUp = null,
    rirOrRpe = false,
    language = null,
    pinnedExercises = [],
    isChecked = false,
    blockWeeks = 0,
    blockCurrentWeek = 0,
    coach = null,
    weekWorkouts = [],
    phone = null,
    notes = "",
    status = "active-string",
    invitationId = "",
    feeHistory = [],
    gender = false,
    blockCurrentName = "",
    weeklyFormId = "",
    blockFormId = "",
    profileSquat = 0,
    profileBenchPress = 0,
    profileDeadlift = 0,
    dietaryPreference = "none",
    allergenCodes = [],
    pendingUserId = "",
    usePendingBlocksPath = false,
    invitationEmail = "",
    invitationCreatedAtMs = null,
    showNutritionPlanTotalsOverride = null
  ) {
    this.id = id;
    this.email = email;
    this.name = name;
    this.surname = surname;
    this.nameForCoach = nameForCoach;
    this.surnameForCoach = surnameForCoach;
    this.bodyweightUnit = bodyweightUnit;
    this.exerciseUnit = exerciseUnit;
    this.lastBackUp = lastBackUp;
    this.language = language;
    this.isChecked = isChecked;
    this.blockWeeks = blockWeeks;
    this.blockCurrentWeek = blockCurrentWeek;
    this.coach = coach;
    this.weekWorkouts = weekWorkouts;
    this.phone = phone;
    this.notes = notes;
    this.status = status;
    this.gender = gender;
    this.blockCurrentName = blockCurrentName;
    this.invitationId = invitationId;
    this.feeHistory = feeHistory;
    this.pinnedExercises = pinnedExercises;
    this.weeklyFormId = weeklyFormId;
    this.blockFormId = blockFormId;
    this.profileSquat = profileSquat;
    this.profileBenchPress = profileBenchPress;
    this.profileDeadlift = profileDeadlift;
    this.dietaryPreference = normalizeUserDietaryPreference(dietaryPreference);
    this.allergenCodes = normalizeAllergenCodes(allergenCodes);
    this.showNutritionPlanTotalsOverride = normalizeNutritionPlanTotalsOverride(
      showNutritionPlanTotalsOverride
    );
    this.pendingUserId = pendingUserId;
    this.usePendingBlocksPath = usePendingBlocksPath;
    this.invitationEmail = invitationEmail;
    this.invitationCreatedAtMs = invitationCreatedAtMs;
    this.clientNutritionPlanStore = new ClientNutritionPlanStore(this);

    makeAutoObservable(this);
  }

  // #region /* CHATS */
  get sortedMessages() {
    const messages = Array.isArray(this.messages)
      ? this.messages
      : Array.from(this.messages);

    return messages
      .filter((message) => message.timestamp != null)
      .sort((a, b) => a.timestamp - b.timestamp);
  }

  get getLastMessage() {
    return this.messages && this.messages.length > 0
      ? this.sortedMessages[this.sortedMessages.length - 1]
      : null;
  }

  get messageLastTimestamp() {
    return (
      this.messages.slice().sort((a, b) => b.timestamp - a.timestamp)[0]
        ?.timestamp ?? null
    );
  }

  get messageFirstTimestamp() {
    return (
      this.messages.slice().sort((a, b) => a.timestamp - b.timestamp)[0]
        ?.timestamp ?? null
    );
  }

  //unused
  async fetchActiveChatBatch() {
    const activeRef = doc(
      db,
      "coaches",
      this.coach.id,
      "chats",
      this.id,
      "batched_messages",
      this.id
    );

    try {
      const snap = await getDoc(activeRef);
      if (!snap.exists()) return;

      const batchData = snap.data();
      const batchMessages = batchData.messages || [];

      // const firestoreIDs = new Set(
      //   batchMessages.map((m) => m.id.toLowerCase())
      // );
      // const localIDs = new Set(this.messages.map((m) => m.id.toLowerCase()));

      // const deletedIDs = [...localIDs].filter((id) => !firestoreIDs.has(id));
      // const firstTs =
      //   batchMessages.length > 0 ? batchMessages[0].timestamp.toDate() : null;

      // // delete stale local messages
      // deletedIDs.forEach((id) => {
      //   const msg = this.messages.find((m) => m.id === id);
      //   if (msg && msg.timestamp <= firstTs && batchMessages.length > 80) {
      //     const idx = this.messages.indexOf(msg);
      //     if (idx > -1) {
      //       this.messages.splice(idx, 1);
      //     }
      //   }
      // });

      // fetch any new ones
      const existingIDs = new Set(this.messages.map((m) => m.id));
      batchMessages.forEach((md) => {
        const id = md.id;
        if (!existingIDs.has(id)) {
          this.fetchMessage(md);
        }
      });

      // Get all blockIds from messages and fetch missing blocks
      // const blockIds = [
      //   ...new Set(this.messages.map((m) => m.blockId).filter(Boolean)),
      // ];
      // // console.log("fetchMissingBlocksFromIDsArray", blockIds);
      // await this.fetchMissingBlocksFromIDsArray(blockIds, t);
    } catch (err) {
      console.error("Failed to fetch active batch:", err);
      throw err;
    }
  }

  async fetchNextChatBatch(t) {
    let q = query(
      collection(
        db,
        "coaches",
        this.coach.id,
        "chats",
        this.id,
        "batched_messages"
      ),
      where("is_active_batch", "==", false)
    );

    if (this.messageFirstTimestamp != null) {
      q = query(
        q,
        where("last_timestamp", "<", this.messageFirstTimestamp),
        orderBy("last_timestamp", "desc")
      );
    } else {
      q = query(q, orderBy("last_timestamp", "desc"));
    }

    q = query(q, limit(1));

    try {
      const snap = await getDocs(q);
      if (snap.empty) return;

      const docSnap = snap.docs[0];
      const messagesArray = docSnap.data().messages || [];
      const existingIDs = new Set(this.messages.map((m) => m.id));

      const newMessages = [];

      messagesArray.forEach((md) => {
        const id = md.id;
        if (!existingIDs.has(id)) {
          this.fetchMessage(md);
          newMessages.push(md); // store new messages
        }
      });

      // Extract and deduplicate blockIds from new messages only
      const blockIds = [
        ...new Set(newMessages.map((m) => m.block_id).filter(Boolean)),
      ];
      // console.log("fetchMissingBlocksFromIDsArray", blockIds);

      if (blockIds.length > 0) {
        await this.fetchMissingBlocksFromIDsArray(blockIds);
      }
    } catch (err) {
      console.error("Failed to fetch next batch:", err);
    }
  }

  async fetchMissingBlocksFromIDsArray(IDsArray) {
    if (!Array.isArray(IDsArray) || IDsArray.length === 0) return [];

    const localBlockIds = new Set(this.blocks.map((block) => block.id));
    const missingIDs = IDsArray.filter((id) => !localBlockIds.has(id));
    if (missingIDs.length === 0) return [];
    console.log("fetchMissingBlocksFromIDsArray", missingIDs);
    const blocksCollectionPath = resolveClientBlocksCollectionPath(this);
    if (!blocksCollectionPath) return [];
    const blocksCollection = collection(db, ...blocksCollectionPath);

    const chunks = [];
    for (let i = 0; i < missingIDs.length; i += 10) {
      chunks.push(missingIDs.slice(i, i + 10));
    }

    const fetchedBlocks = [];

    try {
      for (const chunk of chunks) {
        // Query on the document's `id` field instead of the internal __name__
        const q = query(blocksCollection, where("id", "in", chunk));
        const snapshot = await getDocs(q);
        snapshot.forEach((doc) => {
          const blockData = doc.data();
          // skip if already in this.blocks
          if (this.blocks.find((b) => b.id === blockData.id)) {
            return;
          }
          const block = this.fetchBlock(blockData);
          fetchedBlocks.push(block);
        });
      }
    } catch (error) {
      console.error("Error fetching missing blocks:", error);
    }

    return fetchedBlocks;
  }

  fetchChatLastMessages(chatDocData) {
    // console.log("fetchChatLastMessages ", this.email);

    let didFetchNewMessages = false;

    const newMessages = [];

    if (chatDocData) {
      if (chatDocData.last_messages_from_client) {
        chatDocData.last_messages_from_client.forEach((lastMessageData) => {
          const alreadyExists = this.messages.some(
            (message) =>
              message.id.toLowerCase() === lastMessageData.id.toLowerCase()
          );
          if (!alreadyExists) {
            this.fetchMessage(lastMessageData);
            newMessages.push(lastMessageData);
            didFetchNewMessages = true;
          }
        });
      }
      if (chatDocData.last_messages_from_coach) {
        chatDocData.last_messages_from_coach.forEach((lastMessageData) => {
          const alreadyExists = this.messages.some(
            (message) =>
              message.id.toLowerCase() === lastMessageData.id.toLowerCase()
          );
          if (!alreadyExists) {
            this.fetchMessage(lastMessageData);
            didFetchNewMessages = true;
          }
        });
      }
    }
    if (didFetchNewMessages) {
      const chatManager = ChatManager.getInstance();
      if (this.coach?.selectedChatId === this.id) {
        const blockIds = [
          ...new Set(newMessages.map((m) => m.block_id).filter(Boolean)),
        ];
        // console.log("fetchMissingBlocksFromIDsArray", blockIds);

        if (blockIds.length > 0) {
          this.fetchMissingBlocksFromIDsArray(blockIds);
        }
        setTimeout(() => {
          chatManager.scrollToBottom();
        }, 200);
      }
    }
  }

  fetchMessage(messageData) {
    const newMessage = new ChatMessage(
      String(messageData.id),
      messageData.timestamp.toDate(),
      messageData.text ? messageData.text : "",
      messageData.type,
      messageData.is_from_coach,
      messageData.reply_to ? messageData.reply_to : null,
      messageData.attachment ? messageData.attachment : null,
      messageData.total_time ? messageData.total_time : 0.0,
      messageData.serie_id ? messageData.serie_id : null,
      messageData.workout_id ? messageData.workout_id : null,
      messageData.block_id ? messageData.block_id : null,
      this,
      MessageStatus.SENT
    );

    this.messages.push(newMessage);
  }

  get hasPendingChatOutboxMessages() {
    return this.chatOutboxPendingCount > 0;
  }

  getChatOutboxMessageStatus(record) {
    if (record?.errorStage === ChatOutboxErrorStage.MEDIA) {
      return MessageStatus.ERROR_MEDIA;
    }
    if (record?.status === "error") {
      return MessageStatus.ERROR_MESSAGE;
    }
    return MessageStatus.SENDING;
  }

  createMessageFromChatOutboxRecord(record) {
    return new ChatMessage(
      String(record.id),
      new Date(record.timestampMs),
      record.text ?? "",
      record.type,
      record.isFromCoach !== false,
      record.replyTo ?? null,
      record.attachment ?? null,
      record.totalTime ?? 0.0,
      record.serieId ?? null,
      record.workoutId ?? null,
      record.blockId ?? null,
      this,
      this.getChatOutboxMessageStatus(record),
      record.originalAttachmentURL ?? null,
    );
  }

  addLocalChatMessageIfMissing(message) {
    const messageId = String(message?.id ?? "").toLowerCase();
    if (!messageId) return;

    const alreadyExists = this.messages.some(
      (existingMessage) =>
        String(existingMessage?.id ?? "").toLowerCase() === messageId,
    );

    if (!alreadyExists) {
      this.messages.push(message);
    }
    this.messageIds.add(messageId);
  }

  async refreshChatOutboxPendingCount() {
    if (!this.coach?.id || !this.id) return 0;

    const chatOutboxManager = ChatOutboxManager.getInstance();
    const count = await chatOutboxManager.countForClient(this.coach.id, this.id);
    runInAction(() => {
      this.chatOutboxPendingCount = count;
    });
    return count;
  }

  async restoreChatOutboxMessages() {
    if (!this.coach?.id || !this.id) return [];

    const chatOutboxManager = ChatOutboxManager.getInstance();
    const records = await chatOutboxManager.getMessagesForClient(
      this.coach.id,
      this.id,
    );

    runInAction(() => {
      records.forEach((record) => {
        const messageId = String(record.id ?? "").toLowerCase();
        const existingMessage = this.messages.find(
          (message) => String(message?.id ?? "").toLowerCase() === messageId,
        );

        if (existingMessage) {
          existingMessage.setProperty(
            "status",
            this.getChatOutboxMessageStatus(record),
          );
          if (!existingMessage.attachment && record.attachment) {
            existingMessage.setProperty("attachment", record.attachment);
          }
          return;
        }

        this.messages.push(this.createMessageFromChatOutboxRecord(record));
      });
      this.chatOutboxPendingCount = records.length;
    });

    return records;
  }

  async enqueueOutgoingChatMessages(messages, options = {}) {
    const chatMessages = (Array.isArray(messages) ? messages : [messages]).filter(
      Boolean,
    );
    if (chatMessages.length === 0) return [];

    const groupId = options.groupId || chatMessages[0]?.id?.toString();
    const queuedAtMs = Date.now();
    const records = chatMessages.map((message, index) =>
      buildChatOutboxRecord(message, {
        coachId: this.coach?.id,
        clientId: this.id,
        groupId,
        sequence: index,
        nowMs: queuedAtMs,
      }),
    );

    const chatOutboxManager = ChatOutboxManager.getInstance();
    await chatOutboxManager.enqueueMessages(records);

    runInAction(() => {
      chatMessages.forEach((message) => {
        message.setProperty("status", MessageStatus.SENDING);
        this.addLocalChatMessageIfMissing(message);
      });
    });
    await this.refreshChatOutboxPendingCount();

    if (options.flush !== false) {
      this.flushChatOutbox().catch((error) => {
        console.error("Error flushing chat outbox:", error);
      });
    }

    return records;
  }

  async markChatMessageSent(messageId) {
    const normalizedMessageId = String(messageId ?? "").toLowerCase();
    if (!normalizedMessageId) return;

    const existingMessage = this.messages.find(
      (message) =>
        String(message?.id ?? "").toLowerCase() === normalizedMessageId,
    );
    if (existingMessage) {
      existingMessage.setProperty("status", MessageStatus.SENT);
    }

    const chatOutboxManager = ChatOutboxManager.getInstance();
    await chatOutboxManager.removeMessage(String(messageId));
    await this.refreshChatOutboxPendingCount();
  }

  async retryChatMessage(message) {
    if (!message) return;

    const chatOutboxManager = ChatOutboxManager.getInstance();
    const existingRecord = await chatOutboxManager.getMessage(String(message.id));
    if (!existingRecord) {
      await this.enqueueOutgoingChatMessages([message], { flush: false });
    }

    message.setProperty("status", MessageStatus.SENDING);
    return this.flushChatOutbox({ messageId: String(message.id), force: true });
  }

  async flushChatOutbox({ messageId = null, force = false } = {}) {
    if (!this.coach?.id || !this.id) return;

    const networkManager = NetworkManager.getInstance();
    if (networkManager.isOffline && !networkManager.connectivityVerified) {
      await this.refreshChatOutboxPendingCount();
      return;
    }

    if (!messageId && this.chatOutboxFlushPromise) {
      this.chatOutboxFlushRequested = true;
      return this.chatOutboxFlushPromise;
    }

    const runFlush = async () => {
      const chatOutboxManager = ChatOutboxManager.getInstance();

      const flushRecords = async (records) => {
        for (const record of records) {
          if (!force && messageId && record.nextAttemptAtMs > Date.now()) {
            continue;
          }

          let message = this.messages.find(
            (existingMessage) =>
              String(existingMessage?.id ?? "").toLowerCase() ===
              String(record.id ?? "").toLowerCase(),
          );

          if (!message) {
            message = this.createMessageFromChatOutboxRecord(record);
            runInAction(() => {
              this.addLocalChatMessageIfMissing(message);
            });
          } else if (!message.attachment && record.attachment) {
            message.setProperty("attachment", record.attachment);
          }

          message.setProperty("status", MessageStatus.SENDING);
          await chatOutboxManager.markAttempt(record.id);

          const result = await this.uploadMessageAndAttachment(message, {
            fromOutbox: true,
          });

          if (result?.ok) {
            await this.markChatMessageSent(message.id);
            continue;
          }

          if (result?.queued) {
            return false;
          }

          const error = result?.error || new Error("Chat message upload failed.");
          await chatOutboxManager.markError(record.id, {
            error,
            stage: result?.stage ?? ChatOutboxErrorStage.MESSAGE,
          });
          message.setProperty(
            "status",
            result?.stage === ChatOutboxErrorStage.MEDIA
              ? MessageStatus.ERROR_MEDIA
              : MessageStatus.ERROR_MESSAGE,
          );
        }

        return true;
      };

      if (messageId) {
        const records = [await chatOutboxManager.getMessage(messageId)].filter(
          Boolean,
        );
        await flushRecords(records);
        await this.refreshChatOutboxPendingCount();
        return;
      }

      do {
        this.chatOutboxFlushRequested = false;
        const records = await chatOutboxManager.getReadyMessagesForClient(
          this.coach.id,
          this.id,
        );
        const canContinue = await flushRecords(records);
        await this.refreshChatOutboxPendingCount();

        if (!canContinue) {
          this.chatOutboxFlushRequested = false;
          return;
        }
      } while (this.chatOutboxFlushRequested);
    };

    if (messageId) {
      return runFlush();
    }

    this.chatOutboxFlushPromise = runFlush().finally(() => {
      this.chatOutboxFlushPromise = null;
    });

    return this.chatOutboxFlushPromise;
  }

  async resetUnreadCountAndMarkRead() {
    const chatDocRef = doc(db, "coaches", this.coach.id, "chats", this.id);

    try {
      // 1) update local state immediately
      this.unreadCount = 0;
      this.unreadMark = false;

      // 2) attempt to update Firestore (won't create a new doc)
      // Delete the `unread_mark` field instead of setting it to false so the
      // field is absent when not needed.
      await updateDoc(chatDocRef, {
        unread_count_for_coach: 0,
        unread_mark: deleteField(),
      });
    } catch (error) {
      // Firestore throws a FirebaseError with code 'not-found'
      if (error.code === "not-found") {
        console.log(
          `Cannot reset unread count and mark as read: chat document "${this.id}" for coach "${this.coach.id}" does not exist.`
        );
      } else {
        console.error("Error resetting unread count and mark as read:", error);
      }
    }
  }

  async uploadUnreadMark() {
    const chatDocRef = doc(db, "coaches", this.coach.id, "chats", this.id);

    try {
      // 2) attempt to update Firestore (won't create a new doc)
      // If unreadMark is falsy, delete the field from Firestore; otherwise set it to true.
      if (this.unreadMark) {
        await updateDoc(chatDocRef, { unread_mark: true });
      } else {
        await updateDoc(chatDocRef, { unread_mark: deleteField() });
      }
    } catch (error) {
      // Firestore throws a FirebaseError with code 'not-found'
      if (error.code === "not-found") {
        console.log(
          `Cannot upload unread mark: chat document "${this.id}" for coach "${this.coach.id}" does not exist.`
        );
      } else {
        console.error("Error uploading unread mark:", error);
      }
    }
  }

  async uploadMessageAndAttachment(message, options = {}) {
    if (!options.fromOutbox) {
      await this.enqueueOutgoingChatMessages([message]);
      return { ok: true, queued: true };
    }

    const networkManager = NetworkManager.getInstance();

    if (message.status != MessageStatus.SENDING) {
      message.setProperty("status", MessageStatus.SENDING);
    }

    if (networkManager.isOffline && !networkManager.connectivityVerified) {
      return { ok: false, queued: true };
    }

    if (
      message.type === "audio" ||
      message.type === "video" ||
      message.type === "image"
    ) {
      try {
        const attachmentURL = await this.uploadAttachment(message);
        if (!attachmentURL) {
          message.setProperty("status", MessageStatus.ERROR_MEDIA);
          return {
            ok: false,
            stage: ChatOutboxErrorStage.MEDIA,
            error: new Error("Attachment upload failed."),
          };
        }
      } catch (error) {
        message.setProperty("status", MessageStatus.ERROR_MEDIA);

        console.error("Error uploading attachment:", error);
        return { ok: false, stage: ChatOutboxErrorStage.MEDIA, error };
      }
    }

    try {
      await this.uploadMessage(message);
      message.setProperty("status", MessageStatus.SENT);
      return { ok: true };
    } catch (error) {
      console.error("Error uploading message:", error);
      message.setProperty("status", MessageStatus.ERROR_MESSAGE);
      return { ok: false, stage: ChatOutboxErrorStage.MESSAGE, error };
    }
  }

  async uploadMessage(message) {
    const chatDocRef = doc(db, "coaches", this.coach.id, "chats", this.id);

    const batchDocRef = doc(
      db,
      "coaches",
      this.coach.id,
      "chats",
      this.id,
      "batched_messages",
      this.id
    );

    const messageData = message.uploadToCloud();

    const chatData = {
      last_messages_from_coach: arrayUnion(messageData),
      last_timestamp: serverTimestamp(),
      unread_count_for_client: increment(1),
    };

    const batchData = {
      messages_count: increment(1),
      last_timestamp: serverTimestamp(),
      messages: arrayUnion(messageData),
      client_id: this.id,
      coach_id: this.coach.id,
      is_active_batch: true,
    };

    try {
      await runTransaction(db, async (transaction) => {
        const batchDocSnapshot = await transaction.get(batchDocRef);
        const existingMessages = batchDocSnapshot.exists()
          ? batchDocSnapshot.data()?.messages || []
          : [];

        if (
          !shouldUploadChatMessageToBatch({
            existingMessages,
            messageId: message.id,
          })
        ) {
          return;
        }

        transaction.set(chatDocRef, chatData, { merge: true });
        transaction.set(batchDocRef, batchData, { merge: true });
      });
    } catch (error) {
      console.error("Error committing chat message write:", error);
      throw error;
    }
  }

  async uploadAttachment(message) {
    const type = message.type;
    let fileName,
      folder,
      contentType,
      attachment = message.attachment;

    switch (type) {
      case "image":
        fileName = `${message.id}.jpg`;
        folder = "image_messages";
        contentType = "image/jpeg";
        break;

      case "video":
        if (!message.serieId) {
          console.error("Missing video serieId in message");
          return null;
        }
        fileName = `${message.serieId}.mp4`;
        folder = "video_messages";
        contentType = "video/mp4";
        break;

      case "audio":
        // now storing as .m4a with the audio/x-m4a MIME
        fileName = `${message.id}.m4a`;
        folder = "audio_messages";
        contentType = "audio/x-m4a";
        break;

      default:
        console.error("Unsupported media type:", type);
        return null;
    }

    const storage = getStorage(undefined, "gs://efort-app-storage-eu");
    const storagePath = `coaches/${this.coach.id}/chats/${this.id}/${folder}/${fileName}`;
    const storageRef = ref(storage, storagePath);

    try {
      let downloadURL;

      try {
        downloadURL = await getDownloadURL(storageRef);
        if (downloadURL) {
          return downloadURL;
        }
      } catch (error) {
        if (error?.code && error.code !== "storage/object-not-found") {
          console.warn("Could not check existing chat attachment:", error);
        }
      }

      if (type === "image" || type === "audio") {
        // both image and audio are File/Blob
        await uploadBytes(storageRef, attachment, { contentType });
        downloadURL = await getDownloadURL(storageRef);
        console.log(
          `${type.charAt(0).toUpperCase() + type.slice(1)} uploaded:`,
          downloadURL
        );
      } else {
        // video (attachment is a URI string)
        const resp = await fetch(attachment);
        const blob = await resp.blob();
        const slice = blob.slice(0, blob.size, contentType);
        await uploadBytes(storageRef, slice);
        downloadURL = await getDownloadURL(storageRef);
        console.log(`Video uploaded:`, downloadURL);
      }

      return downloadURL;
    } catch (err) {
      console.error("Error uploading file:", err);
      return null;
    }
  }

  async deleteAllChatData() {
    // Reference to the batched_messages subcollection
    const batchedMessagesColRef = collection(
      db,
      "coaches",
      this.coach.id,
      "chats",
      this.id,
      "batched_messages"
    );

    // Recursively delete documents in batches of 500
    async function deleteDocsInBatches(refCol) {
      while (true) {
        const snap = await getDocs(query(refCol, limit(500)));
        if (snap.empty) break;
        const batch = writeBatch(db);
        snap.docs.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
    }

    await deleteDocsInBatches(batchedMessagesColRef);

    // Delete the chat document itself
    const chatDocRef = doc(db, "coaches", this.coach.id, "chats", this.id);
    try {
      await deleteDoc(chatDocRef);
    } catch (error) {
      console.error("Error deleting chat document:", error);
    }

    // Delete all files from storage in the specified folders
    const folders = [
      "video_messages",
      "audio_messages",
      "image_messages",
      "video_thumbnails",
    ];

    const storage = getStorage(undefined, "gs://efort-app-storage-eu");

    for (const folder of folders) {
      const folderPath = `coaches/${this.coach.id}/chats/${this.id}/${folder}`;
      const listRef = ref(storage, folderPath);
      try {
        const listResult = await listAll(listRef);
        await Promise.all(
          listResult.items.map((itemRef) => deleteObject(itemRef))
        );
      } catch (error) {
        console.error(`Error deleting files in folder ${folder}:`, error);
      }
    }
  }

  // #endregion

  // #region /* FETCHES */
  async fetchBlocksFromCloud() {
    const blocksPathSegments = resolveClientBlocksCollectionPath(this);
    if (!blocksPathSegments) return;

    const blocksRef = collection(db, ...blocksPathSegments);
    const blocksSnapshot = await getDocs(blocksRef);

    if (!blocksSnapshot.empty) {
      const existingBlockIds = new Set(this.blocks.map((block) => block.id));

      runInAction(() => {
        const blocks = blocksSnapshot.docs
          .map((doc) => {
            const blockData = doc.data();
            if (!existingBlockIds.has(blockData.id)) {
              return this.fetchBlock(blockData);
            }
            return null;
          })
          .filter(Boolean); // remove nulls
      });
    }
  }

  async fetchArraysFromCloud() {
    const arrayDocRef = doc(db, "users", this.id, "arrays", "arrays");
    const arraysSnapshot = await getDoc(arrayDocRef);

    if (!arraysSnapshot.exists()) {
      console.warn("No arrays document found for user:", this.id);
      return;
    }

    const arraysData = arraysSnapshot.data();
    runInAction(() => {
      if (arraysData.bodyweights) {
        arraysData.bodyweights.forEach((bodyweightData) => {
          this.fetchBodyweight(bodyweightData);
        });
      }

      if (arraysData.tracs) {
        arraysData.tracs.forEach((tracData) => {
          this.fetchTrac(tracData);
        });
      }

      if (arraysData.notes) {
        arraysData.notes.forEach((noteData) => {
          this.fetchNote(noteData);
        });
      }
    });
  }

  async fetchNutritionPlansFromCloud() {
    if (!this.clientNutritionPlanStore) {
      this.nutritionPlans = [];
      return [];
    }

    this.clientNutritionPlanStore.rebindClientWorkspace?.();
    const plans = await this.clientNutritionPlanStore.fetchPlansFromCloud();
    runInAction(() => {
      this.nutritionPlans = plans;
    });
    return plans;
  }

  async fetchFormsFromCloud() {
    if (shouldSkipUserScopedForms(this)) return;

    const formsColRef = collection(db, "users", this.id, "forms");
    const formsSnapshot = await getDocs(formsColRef);

    if (formsSnapshot.length === 0) {
      console.warn(
        "No outdated or missing last_update forms found for user:",
        this.id
      );
      return;
    }
    runInAction(() => {
      formsSnapshot.forEach((formSnapshot) => {
        const formData = formSnapshot.data();
        // Check if form already exists locally
        const formExists = this.forms.some(
          (f) => f.id.toString().toLowerCase() === formData.id.toLowerCase()
        );
        if (!formExists) {
          this.fetchUserForm(formData);
        }
      });
    });
  }

  async fetchFormsFromCloudBetweenDates(startDate, endDate, formTypes = null) {
    if (shouldSkipUserScopedForms(this)) return;

    // If fetching weekly forms specifically, check optimizations
    const isFetchingWeeklyForms = formTypes && formTypes.includes("Semanal");

    if (isFetchingWeeklyForms) {
      // First, check if we already have a weekly form for this date range locally
      const existingWeeklyForm = this.forms.find((f) => {
        if (f.type !== "Semanal") return false;
        const formDate = new Date(f.date);
        return formDate >= startDate && formDate < endDate;
      });
      if (existingWeeklyForm) {
        return;
      }

      // Second, check if we've already attempted to fetch for this start date
      const startDateKey = startDate.toISOString();
      if (this.fetchedWeeklyFormStartDates.includes(startDateKey)) {
        return;
      }

      // Mark this start date as attempted
      runInAction(() => {
        this.fetchedWeeklyFormStartDates.push(startDateKey);
      });
    }

    // Proceed with the fetch
    const formsColRef = collection(db, "users", this.id, "forms");
    let formsQuery;

    // If formTypes is specified, add a filter for it
    if (formTypes && formTypes.length > 0) {
      formsQuery = query(
        formsColRef,
        where("date", ">=", startDate),
        where("date", "<=", endDate),
        where("type", "in", formTypes)
      );
    } else {
      formsQuery = query(
        formsColRef,
        where("date", ">=", startDate),
        where("date", "<=", endDate)
      );
    }

    const formsSnapshot = await getDocs(formsQuery);

    if (formsSnapshot.empty) {
      console.log(
        "No forms found between the specified dates for user:",
        this.id
      );
      return;
    }
    runInAction(() => {
      formsSnapshot.forEach((formSnapshot) => {
        const formData = formSnapshot.data();
        // Check if form already exists locally
        const formExists = this.forms.some(
          (f) => f.id.toString().toLowerCase() === formData.id.toLowerCase()
        );
        if (!formExists) {
          this.fetchUserForm(formData);
        }
      });
    });
  }

  async fetchFormByBlockId(blockId) {
    if (shouldSkipUserScopedForms(this)) return null;

    // First, check if we already have a form with this blockId locally
    const existingForm = this.forms.find((f) => f.blockId === blockId);
    if (existingForm) {
      return existingForm;
    }

    // Second, check if we've already attempted to fetch this blockId
    if (this.fetchedBlockFormIds.includes(blockId)) {
      return null;
    }

    // Mark this blockId as attempted
    runInAction(() => {
      this.fetchedBlockFormIds.push(blockId);
    });

    // Proceed with the fetch
    const formsColRef = collection(db, "users", this.id, "forms");
    const formsQuery = query(formsColRef, where("block_id", "==", blockId));

    const formsSnapshot = await getDocs(formsQuery);

    if (formsSnapshot.empty) {
      console.warn(
        "No form found with block_id:",
        blockId,
        "for user:",
        this.id
      );
      return null;
    }

    let fetchedForm = null;
    runInAction(() => {
      formsSnapshot.forEach((formSnapshot) => {
        const formData = formSnapshot.data();
        // Check if form already exists locally
        const formExists = this.forms.some(
          (f) => f.id.toString().toLowerCase() === formData.id.toLowerCase()
        );
        if (!formExists) {
          fetchedForm = this.fetchUserForm(formData);
        } else {
          fetchedForm = this.forms.find(
            (f) => f.id.toString().toLowerCase() === formData.id.toLowerCase()
          );
        }
      });
    });

    return fetchedForm;
  }

  async fetchFormsFromCloudAfterDate(afterDate) {
    if (shouldSkipUserScopedForms(this)) return;

    const formsColRef = collection(db, "users", this.id, "forms");
    const formsQuery = query(formsColRef, where("date", ">=", afterDate));

    const formsSnapshot = await getDocs(formsQuery);

    if (formsSnapshot.empty) {
      console.warn(
        "No forms found after date:",
        afterDate,
        "for user:",
        this.id
      );
      return;
    }

    runInAction(() => {
      formsSnapshot.forEach((formSnapshot) => {
        const formData = formSnapshot.data();
        // Check if form already exists locally
        const formExists = this.forms.some(
          (f) => f.id.toString().toLowerCase() === formData.id.toLowerCase()
        );
        if (!formExists) {
          this.fetchUserForm(formData);
        }
      });
    });
  }

  async fetchInit() {
    const localFetchSessionId = this.fetchSessionId;
    this.setProperty("isLoading", true);
    const usesPendingBlockPath = shouldUsePendingBlockPath(this);
    const shouldRunFullFetch = shouldRehydrateFullFetch({
      isFetched: this.isFetched,
      wasPartiallyEvicted: this.wasPartiallyEvicted,
      isOffline: this.isOffline,
    });

    if (shouldRunFullFetch) {
      await this.syncStatusManager.safeOperation("fetchBlocks", () =>
        this.fetchBlocksFromCloud()
      );

      // If the client was unloaded while we were fetching, abort any further initialization.
      if (localFetchSessionId !== this.fetchSessionId) {
        return;
      }

      if (!usesPendingBlockPath) {
        await this.syncStatusManager.safeOperation("fetchArrays", () =>
          this.fetchArraysFromCloud()
        );

        if (localFetchSessionId !== this.fetchSessionId) {
          return;
        }

        //Fetch recent forms (start of last week)
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        await this.fetchFormsFromCloudAfterDate(startOfWeek(oneWeekAgo));

        if (localFetchSessionId !== this.fetchSessionId) {
          return;
        }

        if (this.activeBlock()) {
          this.setSelectedBlock(this.activeBlock());
          this.activeBlock().setProperty(
            "selectedWeek",
            Math.min(
              this.activeBlock().currentWeek(),
              this.activeBlock().getWeeks
            )
          );
          this.activeBlock().setProperty(
            "splitSelectedWeek",
            Math.min(
              this.activeBlock().currentWeek(),
              this.activeBlock().getWeeks
            )
          );
          this.setProperty("selectedMacro", this.activeBlock().label);

          //calculate the offset of this.calendarScrollPosition here
          let offset = 0;
          const macroBlocksList = this.selectedMacroBlocks || [];
          const indexToScroll = macroBlocksList.findIndex(
            (b) => b.id === this.activeBlock().id
          );
          if (indexToScroll > 0) {
            for (let i = 0; i < indexToScroll; i++) {
              const block = macroBlocksList[i];
              const blockWidth = block.getBlockDashboardWidth;
              offset += blockWidth + 16; // include row gap
            }
          }
          this.setProperty("calendarScrollPosition", offset);
        }
      } else {
        this.setProperty("selectedTab", "planification-string");
      }

      this.setProperty("isFetched", true);
      this.setProperty("hasBeenFetchedOnce", true);
      this.setProperty("wasPartiallyEvicted", false);
    }
    await this.syncStatusManager.safeOperation("listenerBlocks", () =>
      this.firestoreListener.setupBlocksListener()
    );

    this.setProperty("isLoading", false);
  }

  resetFetchedState({
    retainPartialBlocks = false,
    maxFutureDatedBlocks = 3,
    maxRecentUndatedBlocks = 4,
    undatedWindowDays = 21,
    maxRecentLastUpdatedBlocks = 3,
    recentLastUpdateWindowDays = 14,
  } = {}) {
    try {
      const isChatOpenForThisClient = this?.coach?.selectedChatId === this.id;

      if (this.hasPendingQueuedWrites?.()) {
        // console.log(
        //   `[ClientWindow] skip resetFetchedState (pending writes) client=${this.id}`
        // );
        return false;
      }

      // console.log(
      //   `[ClientWindow] resetFetchedState client=${this.id} chatOpen=${isChatOpenForThisClient}`
      // );

      const shouldRetainPartialBlocks =
        retainPartialBlocks === true && !isChatOpenForThisClient;
      const retainedBlocks = shouldRetainPartialBlocks
        ? selectRetainedBlocks({
            blocks: this.blocks,
            activeBlockId: this.activeBlock()?.id ?? null,
            now: new Date(),
            maxFutureDatedBlocks,
            maxRecentUndatedBlocks,
            undatedWindowDays,
            maxRecentLastUpdatedBlocks,
            recentLastUpdateWindowDays,
          })
        : [];
      const hasRetainedBlocks = retainedBlocks.length > 0;

      // Invalidate any in-flight async fetchInit flow
      this.fetchSessionId += 1;

      // Cancel pending batched writes timer (do not perform any writes for an unloaded client)
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
        this.batchTimer = null;
      }

      // Detach listeners to prevent memory leaks & background updates
      // Keep the chat listener and its related blocks listener (one-week) if present,
      // because chat can rely on recent blocks/messages being available.
      if (!isChatOpenForThisClient) {
        // console.log(
        //   `[ClientWindow] removing blocks listener client=${this.id}`
        // );
        this.firestoreListener?.removeBlocksListener?.();
      } else {
        // console.log(
        //   `[ClientWindow] keeping blocks listener (chat open) client=${this.id}`
        // );
      }
      this.firestoreListener?.removeFormsListener?.();
      this.firestoreListener?.removeArraysListener?.();
      // Intentionally keep chat and competitions listener

      // Clear heavy in-memory data and reset fetch flags/state
      runInAction(() => {
        this.isFetched = false;
        this.wasPartiallyEvicted = hasRetainedBlocks;
        this.isLoading = false;

        // If blocks listener is the one-week chat listener, preserve blocks to avoid breaking
        // chat context; otherwise clear blocks to free memory.
        if (!isChatOpenForThisClient) {
          this.blocks = hasRetainedBlocks ? retainedBlocks : [];
        }
        this.forms = [];
        this.tracs = [];
        this.bodyweights = [];
        this.notesArray = [];
        // Intentionally preserve competitions
        this.nutritionPlans = [];
        this.nutritionEditingPlanId = null;

        // Intentionally preserve chat state
        if (!(this.messageIds instanceof Set)) {
          this.messageIds = new Set();
        }

        this.selectedMacro = "";
        this.focusedBlock = null;
        this.calendarScrollPosition = 0;
        this.initCalendarScroll = false;
        this.showingBlocksWithoutDate = false;
        this.blockHoverStart = null;
        this.blockHoverSpan = 0;
        this.blockHoverId = null;

        this.summaryStartDate = null;
        this.fetchedBlockFormIds = [];
        this.fetchedWeeklyFormStartDates = [];

        this.selectedTab = "summary-string";

        // Reset only the sync operation flags that correspond to the cleared state
        const removedBlocksListener = !isChatOpenForThisClient;

        this.syncStatusManager?.resetAfterClientUnload?.({
          resetFetchArrays: true,
          resetFetchForms: true,
          resetFetchNutritionPlans: true,
          resetFetchBlocks: removedBlocksListener,
          removedListenerForms: true,
          removedListenerBlocks: removedBlocksListener,
        });

        // Clear pending transaction queues for safety
        this.emptyArrays();
      });

      this.clientNutritionPlanStore?.stopSync?.();

      return true;
    } catch (e) {
      console.warn("Client.resetFetchedState failed:", this?.id, e);
      return false;
    }
  }

  updateClient(clientData) {
    const nextUserId =
      clientData.user_id != null ? String(clientData.user_id).trim() : "";
    if (nextUserId && nextUserId !== this.id) {
      this.setProperty("id", nextUserId);
    }

    this.nameForCoach =
      clientData.name_for_coach || clientData.user_name || this.name;
    this.surnameForCoach =
      clientData.surname_for_coach || clientData.user_surname || this.surname;
    this.isChecked = clientData.is_checked ?? this.isChecked;
    this.dietaryPreference = normalizeUserDietaryPreference(
      clientData.dietary_preference ?? this.dietaryPreference,
    );
    this.allergenCodes = normalizeAllergenCodes(
      clientData.allergen_codes ?? this.allergenCodes
    );
    if (
      Object.prototype.hasOwnProperty.call(
        clientData ?? {},
        "show_nutrition_plan_totals_override"
      )
    ) {
      this.showNutritionPlanTotalsOverride = normalizeNutritionPlanTotalsOverride(
        clientData.show_nutrition_plan_totals_override
      );
    } else {
      this.showNutritionPlanTotalsOverride = null;
    }
    this.weekWorkouts = normalizePendingWorkspaceWeekWorkouts(
      clientData.week_workouts ?? this.weekWorkouts
    );

    syncUntouchedClientProfileDraftFields(this);
  }

  fetchRealTimeArrays(arraysData) {
    try {
    } catch (error) {
      console.error(
        "An unexpected error occurred in fetchRealTimeArrays:",
        error
      );
      throw error;
    }
  }

  fetchRealTimeFormData(formData) {
    const internalForm = this.forms.find(
      (form) => form.id.toString().toLowerCase() === formData.id.toLowerCase()
    );
    if (internalForm) {
      internalForm.updateUserForm(formData);
    } else {
      this.fetchUserForm(formData);
    }
  }

  fetchRealTimeBlockData(blockData) {
    const internalBlock = this.blocks.find(
      (block) =>
        block.id.toString().toLowerCase() === blockData.id.toLowerCase()
    );
    if (internalBlock) {
      if (
        blockData &&
        blockData.last_update &&
        internalBlock.lastUpdate.getTime() <
          blockData?.last_update?.toDate()?.getTime()
      ) {
        this.avoidTransaction = true;
        const startTime = Date.now();

        runInAction(() => {
          internalBlock.updateBlockRealTime(blockData);
        });
        setTimeout(() => {
          this.avoidTransaction = false;
          const endTime = Date.now();
          const duration = endTime - startTime;
          console.log(`Transactions were avoided for ${duration} ms`);
        }, 0);
      }
    } else {
      this.fetchBlock(blockData);
    }
  }

  fetchRealTimeCompetitionData(competitionData) {
    console.log("starting fetchRealTimeCompetitionData");
    try {
      // Check if the competition from Firestore is also locally present
      const normalizedCompetitionDataId =
        competitionData?.id?.toString?.().toLowerCase?.() ?? "";
      const internalCompetition = this.competitions.find(
        (competition) =>
          competition.id.toString().toLowerCase() ===
          normalizedCompetitionDataId
      );

      if (internalCompetition) {
        console.log("internalCompetitionFound");
        if (
          (competitionData &&
            competitionData.last_update &&
            competitionData.last_update.toDate() >
              internalCompetition.lastUpdate) ||
          internalCompetition.lastUpdate === null
        ) {
          this.avoidTransaction = true;
          console.log("Competition requires update", competitionData.name);
          internalCompetition.updateCompetition(competitionData);
          setTimeout(() => {
            this.avoidTransaction = false;
          }, 0);
        } else {
          console.log("Competition is up to date", competitionData.name);
        }
      } else {
        this.fetchCompetition(competitionData);
        console.log("Competition not found locally, fetching from Firestore");
      }
    } catch (error) {
      console.error(
        "An unexpected error occurred in fetchRealTimeCompetitionData:",
        error
      );
      throw error;
    }
  }

  fetchCompetition(competitionData) {
    try {
      const newCompetition = new Competition(
        competitionData.id,
        competitionData.name,
        competitionData.date ? competitionData.date.toDate() : null,
        null,
        this,
        competitionData.weight_class,
        competitionData.competition_class,
        competitionData.competition_level,
        "",
        competitionData.weight_in,
        competitionData.hour,
        competitionData.goals,
        competitionData.notes,
        competitionData.top_end,
        competitionData.minimum_mark,
        competitionData.squat_attempts,
        competitionData.squat_warmups,
        competitionData.bench_press_attempts,
        competitionData.bench_press_warmups,
        competitionData.deadlift_attempts,
        competitionData.deadlift_warmups,
        competitionData.squat_results,
        competitionData.bench_press_results,
        competitionData.deadlift_results,
        competitionData.recap_notes,
        competitionData.last_update?.toDate() ?? null
      );

      this.competitions.push(newCompetition);
      return newCompetition;
    } catch (error) {
      error.competitionId = competitionData.id;
      throw error;
    }
  }

  copyBlock(
    t,
    block,
    entireBlock = true,
    targetCalendarBlock = null,
    showToast
  ) {
    try {
      let newBlock;
      if (targetCalendarBlock) {
        newBlock = targetCalendarBlock;

        newBlock.setProperty("notes", block.notes);

        //Add workouts to deletion this.updateClientDeletionWorkouts(workoutId)
        newBlock.workouts.forEach((workout) => {
          this.updateClientDeletionWorkouts(workout.id.toLowerCase());
        });

        newBlock.setProperty("workouts", []);
        newBlock.setProperty("protocols", []);
      } else {
        newBlock = new Block(
          undefined,
          t("copyOf-string") + " " + block.name,
          block.notes,
          [],
          false,
          false,
          this,
          undefined,
          block.totalWeeks,
          block.label,
          new Date()
        );
      }

      if (block.hiddenWeeks !== undefined) {
        newBlock.setProperty("hiddenWeeks", block.hiddenWeeks);
      }

      if (block.weekNames !== undefined) {
        newBlock.setProperty("weekNames", block.weekNames);
      }

      block.workouts.forEach((workout) => {
        if (!entireBlock && workout.week !== 1) {
          return;
        }
        newBlock.copyWorkout(workout);
      });

      if (block.protocols) {
        block.protocols.forEach((slot) => {
          if (!entireBlock && slot.protocolWeek !== 1) {
            return;
          }

          const newExercise = new Exercise(
            undefined,
            slot.name,
            slot.originalName,
            slot.notes,
            slot.order,
            undefined,
            false,
            null,
            [],
            [],
            undefined,
            newBlock,
            slot.link
          );

          newExercise.protocolSlot = slot.protocolSlot ?? null;
          newExercise.protocolText = slot.protocolText ?? "";
          newExercise.protocolWeek = slot.protocolWeek ?? null;
          newExercise.isSecondaryProtocol = slot.isSecondaryProtocol ?? false;

          if (slot.series) {
            slot.series.forEach((serie) => {
              newExercise.copySerie(serie);
            });
          }

          if (slot.attributes) {
            slot.attributes.forEach((attribute) => {
              newExercise.copyAttribute(attribute);
            });
          }

          newBlock.protocols.push(newExercise);
        });
      }

      // Only push if it's a newly created block or not already present
      if (!targetCalendarBlock) {
        this.blocks.push(newBlock);
      } else {
        const filteredWorkouts = newBlock.workouts.filter(
          (workout) => workout.week <= newBlock.totalWeeks
        );
        const filteredProtocols = newBlock.protocols.filter(
          (protocol) => protocol.protocolWeek <= newBlock.totalWeeks
        );
        newBlock.setProperty("workouts", filteredWorkouts);
        newBlock.setProperty("protocols", filteredProtocols);
      }

      if (showToast) {
        if (targetCalendarBlock) {
          showToast("blockCopiedInCalendar-string", {
            type: "info",
            description: "blockCopiedInCalendarDescription-string",
            variables: {
              blockName: block.name,
            },
          });
        } else {
          showToast("blockCopied-string", {
            type: "info",
            description: "blockCopiedDescription-string",
            variables: {
              blockName: block.name,
            },
          });
        }
      }
      return newBlock;
    } catch (error) {
      console.error("Error copying block:", error);
      throw error;
    }
  }

  fetchBlock(blockData) {
    try {
      const newBlock = new Block(
        String(blockData.id),
        blockData.name,
        blockData.notes,
        [],
        false,
        false,
        this,
        undefined,
        blockData.total_weeks ? blockData.total_weeks : 6,
        blockData.label ? blockData.label : "",
        blockData.last_update?.toDate()
      );

      if (blockData.hidden_weeks !== undefined) {
        newBlock.setProperty("hiddenWeeks", blockData.hidden_weeks);
      }

      if (blockData.week_names !== undefined) {
        newBlock.setProperty("weekNames", blockData.week_names);
      }

      if (blockData.calendar_start_date !== undefined) {
        newBlock.setProperty(
          "calendarStartDate",
          blockData.calendar_start_date.toDate()
        );
      }

      blockData.workouts.forEach((workoutData) => {
        newBlock.fetchWorkout(workoutData);
      });

      if (blockData.protocols) {
        newBlock.fetchBlockProtocols(blockData.protocols);
      } else {
        this.avoidTransaction = true;

        runInAction(() => {
          newBlock.updateOldProtocols(
            blockData.squat_slots,
            "squat_slots",
            blockData.squat_protocols,
            blockData.squat_secondary_protocols,
            newBlock.name
          );
          newBlock.updateOldProtocols(
            blockData.bench_press_slots,
            "bench_press_slots",
            blockData.bench_press_protocols,
            blockData.bench_press_secondary_protocols,
            newBlock.name
          );
          newBlock.updateOldProtocols(
            blockData.deadlift_slots,
            "deadlift_slots",
            blockData.deadlift_protocols,
            blockData.deadlift_secondary_protocols,
            newBlock.name
          );
        });

        setTimeout(() => {
          this.avoidTransaction = false;
        }, 0);
      }

      this.blocks.push(newBlock);

      return newBlock;
    } catch (error) {
      console.error("Error creating block:", error);
      throw error;
    }
  }

  fetchNutritionPlan(planData) {
    return null;
  }

  fetchNote(noteData) {
    try {
      const newNote = new Note(
        noteData.id,
        noteData.date.toDate(),
        noteData.text,
        this
      );
      this.notesArray.push(newNote);

      return newNote;
    } catch (error) {
      console.error("Error creating note:", error);
      throw error;
    }
  }

  fetchTrac(tracData) {
    try {
      const newTrac = new Trac(
        String(tracData.id),
        tracData.date.toDate(),
        tracData.leg_soreness,
        tracData.push_soreness,
        tracData.pull_soreness,
        tracData.motivation,
        tracData.perceived_recovery,
        tracData.sleep_quality_nutrition,
        tracData.technical_comfort,
        this
      );
      this.tracs.push(newTrac);

      return newTrac;
    } catch (error) {
      console.error("Error creating trac:", error);
      throw error;
    }
  }

  fetchBodyweight(bodyweightData) {
    try {
      const newBodyweight = new Bodyweight(
        bodyweightData.id,
        bodyweightData.bodyweight,
        bodyweightData.date.toDate(),
        this
      );

      this.bodyweights.push(newBodyweight);

      return newBodyweight;
    } catch (error) {
      console.error("Error creating bodyweight:", error);
      throw error;
    }
  }

  fetchUserForm(formData) {
    try {
      const newForm = new Form(
        formData.id,
        formData.name ? formData.name : "",
        formData.type ? formData.type : "- - -",
        formData.week_day ? formData.week_day : 0,
        formData.date ? formData.date.toDate() : null,
        [],
        null,
        this,
        formData.original_form_id ? formData.original_form_id : "",
        formData.block_id ? formData.block_id : null
      );
      if (formData.last_update) {
        newForm.lastUpdate = formData.last_update.toDate();
      }
      if (formData.seen_by_coach) {
        newForm.seenByCoach = formData.seen_by_coach;
      }

      if (formData.sections) {
        formData.sections.forEach((sectionData) => {
          newForm.fetchUserSection(sectionData);
        });
      }

      this.forms.push(newForm);
      return newForm;
    } catch (error) {
      console.error("Error creating form", error);
      throw error;
    }
  }
  // #endregion

  // #region /* UPLOADS */
  uploadToCloud() {
    const data = {
      user_id: this.id,
      email: this.email,
      user_name: this.name,
      user_surname: this.surname,
      name_for_coach: this.nameForCoach,
      surname_for_coach: this.surnameForCoach,
      bodyweight_unit: this.bodyweightUnit,
      exercise_unit: this.exerciseUnit,
      last_backup: this.lastBackUp,
      rir_or_rpe: this.rirOrRpe,
      language: this.language,
      exercises_metrics_pinned: this.pinnedExercises,
      is_checked: this.isChecked,
      week_workouts: this.weekWorkouts,
      phone: this.phone,
      notes: this.notes,
      fee_history: this.feeHistory,
      gender: this.gender,
      dietary_preference: normalizeUserDietaryPreference(this.dietaryPreference),
      allergen_codes: normalizeAllergenCodes(this.allergenCodes),
      show_nutrition_plan_totals_override: this.showNutritionPlanTotalsOverride,
      coach_id: this.coach.id,
    };
    return data;
  }

  startOrResetBatchTimer() {
    if (!this.isOffline) {
      if (this.batchTimer) {
        clearTimeout(this.batchTimer);
      }
      this.syncStatusManager.setOperationStatus(
        "performTransaction",
        SyncStatus.WAITING
      );
      this.batchTimer = setTimeout(() => {
        this.syncStatusManager.safeOperation("performTransaction", () =>
          this.performTransaction()
        );
      }, 2000);
    } else {
      this.syncStatusManager.setProperty("hasChangesOffline", true);
    }
  }

  async ensureWritableRoutingBeforeTransaction() {
    if (!shouldUsePendingBlockPath(this)) {
      return { ok: true };
    }

    const coach = this.coach;
    if (!coach || typeof coach !== "object") {
      return { ok: true };
    }

    const invitationId = String(this.invitationId ?? "").trim();
    const invitationSummary = Array.isArray(coach.invitationSummaries)
      ? coach.invitationSummaries.find(
          (summary) => String(summary?.invitationId ?? "").trim() === invitationId
        )
      : null;
    const invitationState = normalizeInvitationState(invitationSummary?.state);
    const mustReconcileAcceptedState =
      invitationState === INVITATION_STATE.ACCEPTED;

    if (
      typeof coach.firestoreListener?.reconcilePendingClientRoutingForSync !==
      "function"
    ) {
      if (mustReconcileAcceptedState) {
        this.syncStatusManager.setProperty("hasChangesOffline", true);
        this.syncStatusManager.setOperationStatus(
          "performTransaction",
          SyncStatus.WAITING
        );
        return {
          ok: false,
          reason: "accepted_unmapped_deferred",
        };
      }
      return { ok: true };
    }

    const reconciliationResult =
      await coach.firestoreListener.reconcilePendingClientRoutingForSync({
      coach,
      client: this,
      forceCloudRead: this.syncStatusManager?.hasChangesOffline === true,
    });

    if (reconciliationResult?.status !== "deferred") {
      return { ok: true };
    }

    this.syncStatusManager.setProperty("hasChangesOffline", true);
    this.syncStatusManager.setOperationStatus("performTransaction", SyncStatus.WAITING);

    console.info("[invitation-offline-sync] deferred_client_transaction", {
      coachId: coach?.id ?? null,
      invitationId: this.invitationId ?? "",
      pendingUserId: this.pendingUserId ?? "",
      reason: reconciliationResult?.reason ?? "accepted_unmapped_deferred",
    });

    return {
      ok: false,
      reason: "accepted_unmapped_deferred",
    };
  }

  async ensureWritableNutritionRouting() {
    this.clientNutritionPlanStore?.rebindClientWorkspace?.();

    if (!shouldUsePendingBlockPath(this)) {
      return { ok: true };
    }

    const coach = this.coach;
    if (!coach || typeof coach !== "object") {
      return { ok: true };
    }

    const invitationId = String(this.invitationId ?? "").trim();
    const invitationSummary = Array.isArray(coach.invitationSummaries)
      ? coach.invitationSummaries.find(
          (summary) => String(summary?.invitationId ?? "").trim() === invitationId
        )
      : null;
    const invitationState = normalizeInvitationState(invitationSummary?.state);
    const mustReconcileAcceptedState =
      invitationState === INVITATION_STATE.ACCEPTED;

    if (
      typeof coach.firestoreListener?.reconcilePendingClientRoutingForSync !==
      "function"
    ) {
      if (mustReconcileAcceptedState) {
        this.syncStatusManager?.setProperty?.("hasChangesOffline", true);
        return {
          ok: false,
          reason: "accepted_unmapped_deferred",
        };
      }
      return { ok: true };
    }

    const reconciliationResult =
      await coach.firestoreListener.reconcilePendingClientRoutingForSync({
        coach,
        client: this,
        forceCloudRead: this.syncStatusManager?.hasChangesOffline === true,
      });

    if (reconciliationResult?.status !== "deferred") {
      this.clientNutritionPlanStore?.rebindClientWorkspace?.();
      return { ok: true };
    }

    this.syncStatusManager?.setProperty?.("hasChangesOffline", true);

    console.info("[invitation-offline-sync] deferred_client_nutrition_write", {
      coachId: coach?.id ?? null,
      invitationId: this.invitationId ?? "",
      pendingUserId: this.pendingUserId ?? "",
      reason: reconciliationResult?.reason ?? "accepted_unmapped_deferred",
    });

    return {
      ok: false,
      reason: "accepted_unmapped_deferred",
    };
  }

  async performTransaction() {
    const writableRoutingCheck =
      await this.ensureWritableRoutingBeforeTransaction();
    if (!writableRoutingCheck?.ok) {
      return writableRoutingCheck;
    }

    let writesCount = 0;
    let promises = [];
    const time = this.batchTimer;

    const networkManager = NetworkManager.getInstance();
    const timestamp = networkManager.getNetworkTime();
    const hasPendingBlockMutations =
      shouldUsePendingBlockPath(this) &&
      ((Array.isArray(this.clientTransactionData) &&
        this.clientTransactionData.length > 0) ||
        (Array.isArray(this.clientDeletionData) &&
          this.clientDeletionData.length > 0));

    if (hasPendingBlockMutations) {
      const pendingWorkspacePath = resolvePendingWorkspaceDocPath(this);
      if (pendingWorkspacePath) {
        const pendingWorkspaceDocRef = doc(db, ...pendingWorkspacePath);
        const pendingWorkspacePatch = buildPendingWorkspaceStatePatch({
          client: this,
          isChecked: this.isChecked,
          weekWorkouts: this.weekWorkouts,
          lastUpdate: timestamp,
        });
        await setDoc(
          pendingWorkspaceDocRef,
          pendingWorkspacePatch,
          { merge: true }
        );
      }
    }

    if (this.clientTransactionData) {
      this.clientTransactionData.forEach((blockId) => {
        const blockDocPath = resolveClientBlockDocPath(this, blockId);
        if (!blockDocPath) return;
        const block = this.blocks.find(
          (block) => block.id.toLowerCase() === blockId.toLowerCase()
        );
        if (!block) return;

        promises.push(
          commitProgramBlockWrite({
            pathSegments: blockDocPath.pathSegments,
            block,
            touchedWorkoutIds: getChangedWorkoutIdsFromBlock(block),
            deletedWorkoutIds: this.clientDeletionWorkouts,
            timestamp,
            context: {
              owner: "client",
              clientId: this.id,
              blockId,
              pathKind: blockDocPath.kind,
            },
          }).catch((e) => {
            console.warn("Transaction blocks failed:", e);
            throw e;
          })
        );
      });
    }
    if (this.clientDeletionData) {
      this.clientDeletionData.forEach((blockId) => {
        const blockDocPath = resolveClientBlockDocPath(this, blockId);
        if (!blockDocPath) return;

        promises.push(
          deleteProgramBlock({
            pathSegments: blockDocPath.pathSegments,
            timestamp,
            context: {
              owner: "client",
              clientId: this.id,
              blockId,
              pathKind: blockDocPath.kind,
            },
          }).catch((e) => {
            console.error(`Failed to delete block ${blockId}:`, e);
            throw e;
          })
        );
      });
    }

    if (
      this.clientTransactionNotes.length > 0 ||
      this.clientDeletionNotes.length > 0
    ) {
      const arrayDocRef = doc(db, "users", this.id, "arrays", "arrays");

      promises.push(
        runTransaction(db, async (transaction) => {
          const arrayDocSnapshot = await transaction.get(arrayDocRef);
          let firestoreNotes = arrayDocSnapshot.data()?.notes || [];

          if (this.clientTransactionNotes.length > 0) {
            this.clientTransactionNotes.forEach((notesId) => {
              const internalNote = this.notesArray.find(
                (n) => n.id.toString().toLowerCase() === notesId.toLowerCase()
              );
              if (internalNote) {
                const data = internalNote.uploadToCloud();
                const index = firestoreNotes.findIndex((n) => n.id === notesId);
                if (index !== -1) {
                  firestoreNotes[index] = data;
                } else {
                  firestoreNotes.push(data);
                }
              }
            });
          }

          if (this.clientDeletionNotes.length > 0) {
            this.clientDeletionNotes.forEach((notesId) => {
              firestoreNotes = firestoreNotes.filter((n) => n.id !== notesId);
            });
          }

          transaction.set(
            arrayDocRef,
            { notes: firestoreNotes },
            { merge: true }
          );
        })
      );
    }

    if (
      this.clientTransactionCompetitions.length > 0 ||
      this.clientDeletionCompetitions.length > 0
    ) {
      this.clientTransactionCompetitions.forEach((competitionId) => {
        const competitionDocRef = doc(
          db,
          "users",
          this.id,
          "competitions",
          competitionId.toLowerCase()
        );

        promises.push(
          runTransaction(db, async (transaction) => {
            const competition = this.competitions.find(
              (comp) => comp.id.toLowerCase() === competitionId.toLowerCase()
            );
            const competitionDoc = await transaction.get(competitionDocRef);
            const competitionDocData = competitionDoc.data();

            if (!competitionDocData) {
              transaction.set(
                competitionDocRef,
                competition.uploadToCloud(timestamp)
              );
            } else {
              competition.updateFirestoreCompetitionData(
                competitionDocData,
                timestamp
              );
              transaction.set(competitionDocRef, competitionDocData);
            }
          }).catch((e) => {
            propagateCompetitionTransactionError({
              error: e,
              competitionId,
            });
          })
        );
      });

      this.clientDeletionCompetitions.forEach((competitionId) => {
        const competitionDocRef = doc(
          db,
          "users",
          this.id,
          "competitions",
          competitionId
        );

        promises.push(
          deleteDoc(competitionDocRef).catch((e) => {
            console.error(`Failed to delete competition ${competitionId}:`, e);
            throw e;
          })
        );
      });
    }

    if (this.clientTransactionForms.length > 0) {
      //Not used
      this.clientTransactionForms.forEach((formId) => {
        const formDocRef = doc(
          db,
          "users",
          this.id,
          "forms",
          formId.toLowerCase()
        );

        promises.push(
          runTransaction(db, async (transaction) => {
            const form = this.forms.find(
              (f) => f.id.toLowerCase() === formId.toLowerCase()
            );
            const formDoc = await transaction.get(formDocRef);
            const formDocData = formDoc.data();

            if (formDocData) {
              form.updateFirestoreFormData(formDocData);
              transaction.set(formDocRef, formDocData);
              writesCount += 1;
            }
          }).catch((e) => {
            console.error("Transaction failed:", e);
          })
        );
      });
    }

    if (this.clientDeletionForms.length > 0) {
      const batch = writeBatch(db);

      this.clientDeletionForms.forEach((formId) => {
        const formDocRef = doc(
          db,
          "users",
          this.id,
          "forms",
          formId.toLowerCase()
        );
        batch.delete(formDocRef); // Delete form from Firestore
      });

      promises.push(
        batch.commit().catch((error) => {
          console.error("Error in batch deletion:", error);
        })
      );
    }

    if (
      this.clientTransactionNutritionPlans.length > 0 ||
      this.clientDeletionNutritionPlans.length > 0
    ) {
      const plansDocRef = doc(db, "users", this.id, "nutrition", "nutrition");

      promises.push(
        runTransaction(db, async (transaction) => {
          const plansDocSnapshot = await transaction.get(plansDocRef);
          let firestorePlans = plansDocSnapshot.data()?.nutrition_plans || [];

          if (this.clientTransactionNutritionPlans.length > 0) {
            this.clientTransactionNutritionPlans.forEach((planId) => {
              const internalPlan = this.nutritionPlans.find(
                (np) => np.id.toString().toLowerCase() === planId.toLowerCase()
              );
              if (internalPlan) {
                const data = internalPlan.uploadToCloud();
                const index = firestorePlans.findIndex(
                  (np) => np.id === planId
                );
                if (index !== -1) {
                  firestorePlans[index] = data;
                } else {
                  firestorePlans.push(data);
                }
              }
            });
          }

          if (this.clientDeletionNutritionPlans.length > 0) {
            this.clientDeletionNutritionPlans.forEach((planId) => {
              firestorePlans = firestorePlans.filter((np) => np.id !== planId);
            });
          }

          transaction.set(
            plansDocRef,
            { nutrition_plans: firestorePlans },
            { merge: true }
          );
        })
      );
    }

    if (this.clientTransactionProfile.length > 0) {
      const clientDocRef = doc(db, "users", this.id);

      promises.push(
        updateDoc(clientDocRef, {
          notes: this.notes,
        }).catch((e) => {
          console.error("Failed to update client profile:", e);
          throw e;
        })
      );
    }

    const results = await Promise.allSettled(promises);

    const hasError = hasRejectedTransactionResults(results);

    if (hasError) {
      console.error("Error with performTransaction: ", results);
      throw results;
    } else {
      if (time === this.batchTimer) {
        this.clientTransactionData.forEach((blockId) => {
          const lowerCaseBlockId = blockId.toString().toLowerCase();
          const block = this.blocks.find(
            (b) => b.id.toString().toLowerCase() === lowerCaseBlockId
          );
          if (block && block.workouts) {
            block.workouts.forEach((workout) => {
              if (workout.hasChanged) {
                workout.setProperty("hasChanged", false);
              }
            });
          }
          if (block) {
            block.setProperty("lastUpdate", timestamp);
          }
        });

        this.clientTransactionCompetitions.forEach((compId) => {
          const competition = this.competitions.find(
            (c) =>
              c.id.toString().toLowerCase() === compId.toString().toLowerCase()
          );
          if (competition) {
            competition.setProperty("lastUpdate", timestamp);
          }
        });

        this.emptyArrays();
      } else {
        console.warn(
          "PerformTransaction needs to be performed again due to unsaved changes"
        );
      }
    }

    if (hasPendingBlockMutations) {
      const shouldRefreshPendingWorkspaceDiscoverability =
        shouldRefreshPendingWorkspaceDiscoverabilityAfterMutation({
          client: this,
          coachClients: this.coach?.clients,
        });

      if (
        shouldRefreshPendingWorkspaceDiscoverability &&
        typeof this.coach?.refreshPendingWorkspaceSummariesFromCloud === "function"
      ) {
        void this.coach
          .refreshPendingWorkspaceSummariesFromCloud({
            reason: "pending_mutation_without_visible_invitation",
          })
          .catch((error) => {
            console.warn(
              "[pending-workspace] refresh_after_pending_mutation_failed",
              {
                coachId: this.coach?.id,
                pendingUserId: this.pendingUserId,
                error: error?.message ?? error,
              }
            );
          });
      }
    }

    if (!this.syncStatusManager.hasChangesOffline) {
      /* UPDATE COACH DOC WITH LAST */
      const userCoachRef = doc(db, "coaches", this.coach.id);
      try {
        await updateDoc(userCoachRef, {
          last_backup: serverTimestamp(),
          number_of_clients: this.coach.activeClients.length,
        });
        console.log("Coach document updated successfully");
      } catch (e) {
        console.error("Error updating coach document:", e);
      }

      // /* UPDATE CLIENT DOC WITH COACH INFO */
      // const clientDocRef = doc(db, "users", this.id);
      // const data = {
      //   ...(this.coach.name != "" && { coach_name: this.coach.name }),
      //   ...(this.coach.surname != "" && { coach_surname: this.coach.surname }),
      //   ...(this.coach.email != "" && { coach_email: this.coach.email }),
      // };

      // try {
      //   await updateDoc(clientDocRef, data);
      //   console.log("Client document updated with coach info");
      // } catch (error) {
      //   console.error("Error updating client document:", error);
      // }
    }
  }
  async copyBlockToClient(t, block, entireBlock = false) {
    const newBlock = this.copyBlock(t, block, entireBlock, undefined);

    setTimeout(() => {
      this.showToast("blockCopiedToClient-string", {
        type: "info",
        description: "blockCopiedToClientDescription-string",
        variables: {
          blockName: block.name,
          clientName: `${this.nameForCoach} ${this.surnameForCoach}`,
        },
      });
    }, 2000);

    this.updateClientTransactionData(newBlock.id);

    this.startOrResetBatchTimer();
  }

  async movePendingBlockToLibraryTransactional({
    block,
    destinationBlockId,
    destinationBlockData,
    showToast,
  } = {}) {
    if (this.isOffline) {
      return { ok: false, reason: "offline" };
    }

    if (!shouldUsePendingBlockPath(this)) {
      return { ok: false, reason: "invalid_pending_context" };
    }

    const normalizedCoachId = String(this.coach?.id ?? "").trim();
    const normalizedBlockId = String(block?.id ?? "").trim().toLowerCase();
    const normalizedDestinationBlockId = String(destinationBlockId ?? "")
      .trim()
      .toLowerCase();

    if (!normalizedCoachId || !normalizedBlockId || !normalizedDestinationBlockId) {
      return { ok: false, reason: "invalid_ids" };
    }

    if (!destinationBlockData || typeof destinationBlockData !== "object") {
      return { ok: false, reason: "invalid_destination_payload" };
    }

    const sourceBlockDocPath = resolveClientBlockDocPath(this, normalizedBlockId);
    if (!sourceBlockDocPath) {
      return { ok: false, reason: "invalid_source_path" };
    }

    const timestamp =
      typeof window !== "undefined"
        ? NetworkManager.getInstance().getNetworkTime()
        : new Date();
    const targetBlockDocPath = [
      "coaches",
      normalizedCoachId,
      "library",
      "library",
      "block_library",
      normalizedDestinationBlockId,
    ];

    try {
      await commitProgramBlockTransaction({
        timestamp,
        context: {
          owner: "pending_block_move",
          coachId: normalizedCoachId,
          pendingUserId: this.pendingUserId,
          sourceBlockId: normalizedBlockId,
          destinationBlockId: normalizedDestinationBlockId,
        },
        operations: [
          {
            type: "upsert",
            pathSegments: targetBlockDocPath,
            localBlockData: destinationBlockData,
            allWorkoutsTouched: true,
            replaceNestedArrays: true,
            isLibraryBlock: true,
          },
          {
            type: "delete",
            pathSegments: sourceBlockDocPath.pathSegments,
            requireExists: true,
          },
        ],
      });

      if (typeof showToast === "function") {
        showToast("blockExportedToLibrary-string", {
          type: "info",
          description: "blockExportedToLibraryDescription-string",
          variables: {
            blockName: String(block?.name ?? ""),
          },
        });
      }

      return {
        ok: true,
        movedBlockId: normalizedDestinationBlockId,
        movedBlockData: destinationBlockData,
      };
    } catch (error) {
      console.error("[pending-block-move] transaction_failed", {
        coachId: normalizedCoachId,
        pendingUserId: this.pendingUserId,
        blockId: normalizedBlockId,
        error: error?.message ?? error,
      });
      return {
        ok: false,
        reason: "transaction_failed",
        error,
      };
    }
  }

  async deletePendingBlockTransactional({ blockId } = {}) {
    if (this.isOffline) {
      return { ok: false, reason: "offline" };
    }

    if (!shouldUsePendingBlockPath(this)) {
      return { ok: false, reason: "invalid_pending_context" };
    }

    const normalizedBlockId = String(blockId ?? "").trim().toLowerCase();
    if (!normalizedBlockId) {
      return { ok: false, reason: "invalid_block_id" };
    }

    const sourceBlockDocPath = resolveClientBlockDocPath(this, normalizedBlockId);
    if (!sourceBlockDocPath) {
      return { ok: false, reason: "invalid_source_path" };
    }

    let didDelete = false;

    try {
      const timestamp =
        typeof window !== "undefined"
          ? NetworkManager.getInstance().getNetworkTime()
          : new Date();
      const result = await deleteProgramBlock({
        pathSegments: sourceBlockDocPath.pathSegments,
        timestamp,
        context: {
          owner: "pending_block_delete",
          coachId: this.coach?.id ?? null,
          pendingUserId: this.pendingUserId ?? "",
          blockId: normalizedBlockId,
        },
      });
      didDelete = result?.operations?.[0]?.didDelete === true;

      return {
        ok: true,
        didDelete,
      };
    } catch (error) {
      console.error("[pending-block-delete] transaction_failed", {
        coachId: this.coach?.id ?? null,
        pendingUserId: this.pendingUserId ?? "",
        blockId: normalizedBlockId,
        error: error?.message ?? error,
      });
      return {
        ok: false,
        reason: "transaction_failed",
        error,
      };
    }
  }

  async persistPendingWorkspaceChecked(isChecked) {
    if (!isWorkablePendingWorkspaceClient(this)) {
      return false;
    }

    const pendingWorkspacePath = resolvePendingWorkspaceDocPath(this);
    if (!pendingWorkspacePath) {
      return false;
    }

    const networkManager = NetworkManager.getInstance();
    const timestamp = networkManager.getNetworkTime();
    const pendingWorkspaceDocRef = doc(db, ...pendingWorkspacePath);

    await setDoc(
      pendingWorkspaceDocRef,
      buildPendingWorkspaceStatePatch({
        client: this,
        isChecked,
        lastUpdate: timestamp,
      }),
      { merge: true }
    );

    return true;
  }

  async persistPendingWorkspaceWeekWorkouts(weekWorkouts) {
    if (!isWorkablePendingWorkspaceClient(this)) {
      return false;
    }

    const pendingWorkspacePath = resolvePendingWorkspaceDocPath(this);
    if (!pendingWorkspacePath) {
      return false;
    }

    const networkManager = NetworkManager.getInstance();
    const timestamp = networkManager.getNetworkTime();
    const pendingWorkspaceDocRef = doc(db, ...pendingWorkspacePath);

    await setDoc(
      pendingWorkspaceDocRef,
      buildPendingWorkspaceStatePatch({
        client: this,
        weekWorkouts,
        lastUpdate: timestamp,
      }),
      { merge: true }
    );

    return true;
  }

  uploadData(data) {
    const userDocRef = doc(db, "users", this.id);
    return setDoc(userDocRef, data, { merge: true });
  }
  // #endregion

  // #region /* BLOCKS */
  get sortedBlocks() {
    return this.blocks
      .slice() // Create a shallow copy to prevent modifying the original array
      .sort((a, b) => {
        // Sort by isActive status first
        // if (a.isActive && !b.isActive) return -1;
        // if (!a.isActive && b.isActive) return 1;

        // Handle sorting by the first workout date if isActive status is the same
        let aDate =
          a.initialDate instanceof Date
            ? a.initialDate
            : new Date(a.initialDate);
        let bDate =
          b.initialDate instanceof Date
            ? b.initialDate
            : new Date(b.initialDate);

        // Adjust for blocks without valid dates
        if (a.initialDate === null) aDate = new Date(-8640000000000000); // Use a far future date
        if (b.initialDate === null) bDate = new Date(-8640000000000000); // Use a far future date

        // Compare dates
        return bDate - aDate;
      });
  }

  get sortedLabels() {
    const labels = this.sortedBlocks
      .map((block) => block.label) // Extract the label for each block
      .filter(
        (label, index, self) =>
          label !== null && label !== undefined && self.indexOf(label) === index
      ); // Include empty string but filter out null/undefined

    return labels;
  }

  getFilteredLabels(labelName) {
    const searchTermLower = labelName.toLowerCase();

    // First, filter the workouts based on the search term if provided
    const filteredLabels = this.sortedLabels.filter(
      (label) =>
        (labelName ? label.toLowerCase().includes(searchTermLower) : true) &&
        label !== ""
    );

    return filteredLabels;
  }

  sortedBlocksInLabel(label) {
    return this.blocks
      .filter((block) => block.label === label) // Filter blocks with the specific label
      .slice() // Create a shallow copy
      .sort((a, b) => {
        // Sort by isActive status first
        if (a.isActive && !b.isActive) return -1;
        if (!a.isActive && b.isActive) return 1;

        // Handle sorting by the first workout date if isActive status is the same
        let aDate =
          a.initialDate instanceof Date
            ? a.initialDate
            : new Date(a.initialDate);
        let bDate =
          b.initialDate instanceof Date
            ? b.initialDate
            : new Date(b.initialDate);

        // Adjust for blocks without valid dates
        if (a.initialDate === null) aDate = new Date(-8640000000000000); // Use a far future date
        if (b.initialDate === null) bDate = new Date(-8640000000000000); // Use a far future date

        // Compare dates
        return bDate - aDate;
      });
  }

  get sortedBlocksByDate() {
    return this.blocks
      .filter((block) => block.workouts.some((workout) => workout.date)) // Filter blocks with at least one workout with a date
      .slice() // Create a shallow copy to prevent modifying the original array
      .sort((a, b) => {
        let aDate = new Date(a.initialDate);
        let bDate = new Date(b.initialDate);

        // Compare dates
        return bDate - aDate;
      });
  }

  get sortedBlocksByDateAsc() {
    return this.blocks
      .filter((block) => block.workouts.some((workout) => workout.date)) // Filter blocks with at least one workout with a date
      .slice() // Create a shallow copy to prevent modifying the original array
      .sort((a, b) => {
        let aDate = new Date(a.initialDate);
        let bDate = new Date(b.initialDate);

        // Compare dates
        return aDate - bDate;
      });
  }

  get sortedBlocksByDateWeekCalendar() {
    const allBlocks = [
      ...this.blocks.filter(
        (block) =>
          (block.workouts.some((workout) => workout.date) ||
            block.calendarStartDate) &&
          !(
            block.id === this.blockHoverId &&
            !block?.workouts?.some((w) => w.date)
          )
      ), // Filter `Block` instances with at least one workout with a date
    ];

    const selectedYear = this.weekCalendarYear;

    // Filter the blocks to only include those overlapping with selectedYear
    const filteredBlocks = allBlocks.filter((block) => {
      const startDate = new Date(block.initialDate);
      const endDate = block.endDate();

      // The block is included if its start year or end year matches the selected year
      const blockStartYear = startDate.getFullYear();
      const blockEndYear = endDate.getFullYear();
      return blockStartYear === selectedYear || blockEndYear === selectedYear;
    });

    return filteredBlocks
      .slice() // Create a shallow copy to prevent modifying the original array
      .sort((a, b) => {
        let aDate = new Date(a.initialDate);
        let bDate = new Date(b.initialDate);

        // Compare dates
        return aDate - bDate;
      });
  }

  get blocksWithoutDate() {
    const allBlocksWithoutDate = [
      ...this.blocks.filter((block) => !block.initialDate),
    ];

    return allBlocksWithoutDate;
  }

  setSelectedBlock(selectedBlock) {
    this.blocks.forEach((block) => {
      if (block.id === selectedBlock?.id) {
        block.setProperty("isSelected", true);
      } else {
        block.setProperty("isSelected", false);
      }
    });
  }
  deleteBlock(block) {
    // Find the index of the block to be deleted
    const index = this.blocks.findIndex((b) => b === block);

    const blockForm = this.forms.find((f) => f.blockId === block.id);

    // Check if the block was found
    if (index > -1) {
      if (!this.clientDeletionData.includes(block.id)) {
        this.clientDeletionData.push(block.id);
      }

      if (blockForm) {
        this.updateClientDeletionForms(blockForm.id);
      }
      // Remove the block from the array
      this.blocks.splice(index, 1);

      // if (!this.activeBlock()) {
      //   this.blockCurrentWeek = 0;
      //   this.blockWeeks = 0;
      //   this.uploadData({
      //     block_current_week: 0,
      //     block_weeks: 0,
      //   });
      // }

      this.startOrResetBatchTimer();
    } else {
      console.log("Block not found");
    }
  }

  setShownBlock(shownBlock) {
    this.blocks.forEach((block) => {
      if (block.id === shownBlock.id) {
        block.setIsShown(!block.isShown);
      } else {
        block.setIsShown(false);
      }
    });
  }

  get getBlockCurrentName() {
    if (this.activeBlock()) {
      return this.activeBlock().name;
    } else {
      return this.blockCurrentName;
    }
  }

  get getBlockWeeks() {
    if (this.activeBlock()) {
      return this.activeBlock().totalWeeks;
    } else {
      return this.blockWeeks;
    }
  }

  get getBlockCurrentWeekPrepared() {
    if (this.activeBlock()) {
      return this.activeBlock().currentWeek() > this.activeBlock().getWeeks
        ? false
        : true;
    } else {
      return this.blockCurrentWeekPrepared;
    }
  }

  get getBlockCurrentWeek() {
    if (this.activeBlock()) {
      return this.activeBlock().currentWeek();
    } else {
      return this.blockCurrentWeek;
    }
  }

  activeBlock(startDate = new Date()) {
    const thisWeekStart = startOfWeek(startDate);
    const thisWeekEnd = endOfWeek(startDate);

    // 1) Check if there are any workouts this week
    const blockWithWorkoutThisWeek = this.blocks.find((block) =>
      block.workouts.some((w) => {
        if (!w?.date) return false;
        const d = w.date instanceof Date ? w.date : new Date(w.date);
        return d >= thisWeekStart && d <= thisWeekEnd;
      })
    );

    if (blockWithWorkoutThisWeek) {
      return blockWithWorkoutThisWeek;
    }

    // 2) No workouts this week, search for workouts before this week
    const blocksWithWorkoutsBeforeThisWeek = this.blocks.filter((block) =>
      block.workouts.some((w) => {
        if (!w?.date) return false;
        const d = w.date instanceof Date ? w.date : new Date(w.date);
        return d < thisWeekStart;
      })
    );

    if (blocksWithWorkoutsBeforeThisWeek.length > 0) {
      // 3) Find the most recent workout before this week
      let mostRecentWorkout = null;
      let mostRecentBlock = null;

      for (const block of blocksWithWorkoutsBeforeThisWeek) {
        for (const workout of block.workouts) {
          if (!workout?.date) continue;
          const d =
            workout.date instanceof Date
              ? workout.date
              : new Date(workout.date);
          if (
            d < thisWeekStart &&
            (!mostRecentWorkout || d > mostRecentWorkout.date)
          ) {
            mostRecentWorkout = workout;
            mostRecentBlock = block;
          }
        }
      }

      if (mostRecentBlock) {
        // 4) Find the workout with maximum week that has a date in this block
        const workoutWithMaxWeek = mostRecentBlock.workouts
          .filter((w) => w.date != null && w.week != null)
          .reduce((maxWorkout, currentWorkout) => {
            if (!maxWorkout || currentWorkout.week > maxWorkout.week) {
              return currentWorkout;
            }
            return maxWorkout;
          }, null);

        if (workoutWithMaxWeek) {
          // 5) Calculate pending weeks and estimated end date
          const pendingWeeks =
            mostRecentBlock.totalWeeks - workoutWithMaxWeek.week;
          const lastWorkoutDate =
            workoutWithMaxWeek.date instanceof Date
              ? workoutWithMaxWeek.date
              : new Date(workoutWithMaxWeek.date);

          const estimatedEndDate = new Date(lastWorkoutDate);
          estimatedEndDate.setDate(
            estimatedEndDate.getDate() + pendingWeeks * 7
          );

          // 6) Check if estimated end date falls within the current week or after
          if (estimatedEndDate >= thisWeekStart) {
            return mostRecentBlock;
          }
        }
      }
    }

    // 7) Check blocks with calendarStartDate before returning null
    // Only consider blocks that have NO workouts with dates
    const blocksWithCalendarStartDate = this.blocks.filter(
      (block) =>
        block.calendarStartDate != null &&
        !block.workouts.some((w) => w.date != null)
    );

    for (const block of blocksWithCalendarStartDate) {
      const startDate = block.calendarStartDate;

      const calendarStartWeek = startOfWeek(startDate);

      // Check if the calendar start is not in the future
      if (calendarStartWeek > thisWeekStart) {
        continue;
      }

      // Calculate estimated end date by adding totalWeeks * 7 days
      const estimatedBlockEndDate = new Date(startDate);
      estimatedBlockEndDate.setDate(
        estimatedBlockEndDate.getDate() + (block.totalWeeks - 1) * 7
      );

      // Check if estimated end date falls within the current week or after
      if (estimatedBlockEndDate >= thisWeekStart) {
        return block;
      }
    }

    return null;
  }

  get selectedBlock() {
    return this.blocks.find((block) => block.isSelected === true);
  }
  get shownBlock() {
    return this.blocks.find((block) => block.isShown === true);
  }

  get activeBlockMinDate() {
    if (this.activeBlock() && this.activeBlock().workouts.length > 0) {
      const validDates = this.activeBlock()
        .workouts.filter((workout) => workout.date)
        .map((workout) => new Date(workout.date).getTime());
      if (validDates.length === 0) return null;
      const minTimestamp = Math.min(...validDates);
      return new Date(minTimestamp).getTime();
    }
    return new Date();
  }

  get activeBlockMaxDate() {
    if (this.activeBlock() && this.activeBlock().workouts.length > 0) {
      // Filter out workouts without a date and find the workout with the latest date
      const latestWorkout = this.activeBlock()
        .workouts.filter((workout) => workout.date)
        .reduce(
          (latest, workout) => {
            const workoutDate = new Date(workout.date).getTime();
            return workoutDate > latest.date
              ? { date: workoutDate, week: workout.week }
              : latest;
          },
          { date: 0, week: 0 }
        );

      if (latestWorkout.date === 0) {
        return null; // No valid latest workout found
      }

      // Calculate the estimated end date
      const totalWeeks = this.activeBlock().totalWeeks;
      const remainingWeeks = totalWeeks - latestWorkout.week;
      const estimatedEndDate = new Date(latestWorkout.date);
      estimatedEndDate.setDate(estimatedEndDate.getDate() + remainingWeeks * 7);

      return estimatedEndDate.getTime(); // Return as timestamp
    }
    return null; // or a default date if needed
  }

  getSlotName(exerciseString) {
    const slotMap = {
      "squat-string": "squatSlot",
      "benchPress-string": "benchPressSlot",
      "deadlift-string": "deadliftSlot",
    };

    return slotMap[exerciseString] || null; // Returns null if the input is not in the map
  }

  getWorkouts() {
    const allWorkouts = this.blocks.map((block) => block.workouts).flat(); // Flatten the array of arrays into a single array

    return allWorkouts;
  }

  getAllExercises(t, language) {
    const allExercises = this.blocks
      .map((block) => block.workouts) // Simplify workout retrieval
      .flat() // Flatten block workouts before processing
      .map((workout) => workout.exercises) // Get exercises from each workout
      .flat(); // Flatten exercises into a single array

    // Use Set to automatically remove duplicates based on object reference
    const uniqueExercises = new Set(
      allExercises.map((exercise) => exercise.getName(t, language))
    );

    // Convert Set back to an array of names
    return Array.from(uniqueExercises);
  }

  getExercisesForHistory(selectedWorkout, t, language, options = {}) {
    const targetWorkout =
      options?.selectedWorkout ??
      selectedWorkout ??
      this.selectedBlock?.selectedWorkout ??
      null;
    const targetExercise =
      options?.exercise ??
      targetWorkout?.selectedExercise ??
      targetWorkout?.selectedExerciseToAdd ??
      this.selectedBlock?.selectedWorkout?.selectedExercise ??
      this.selectedBlock?.selectedWorkout?.selectedExerciseToAdd ??
      null;

    if (!targetWorkout || !targetExercise) {
      return [];
    }

    const exerciseName = targetExercise.getName(t, language);
    const selectedWorkoutDate = targetWorkout?.date ?? null;

    // Sort blocks descending by the most recent workout date in each block
    const sortedBlocks = this.blocks
      .filter((block) =>
        block.workouts.some((workout) =>
          workout.exercises.some((exercise) => {
            // first match the name...
            if (exercise.getName(t, language) !== exerciseName) return false;

            // if there's a selected date, only keep past workouts;
            // otherwise accept all dates
            if (selectedWorkoutDate) {
              return workout.date && workout.date < selectedWorkoutDate;
            }
            return true;
          })
        )
      )
      .sort((a, b) => {
        const aMaxDate = Math.max(
          ...a.workouts.flatMap((workout) =>
            workout.date ? [new Date(workout.date).getTime()] : []
          )
        );
        const bMaxDate = Math.max(
          ...b.workouts.flatMap((workout) =>
            workout.date ? [new Date(workout.date).getTime()] : []
          )
        );
        return bMaxDate - aMaxDate;
      });

    // Select the range of blocks (current block + previous ones)
    const limitBlocks = options?.limitBlocks;
    const blocksToInclude = sortedBlocks.slice(
      0,
      Number.isFinite(limitBlocks) ? limitBlocks : this.shownBlocksInHistory
    );

    const sortedExercises = blocksToInclude.flatMap((block) =>
      block.workouts.flatMap((workout) =>
        workout.exercises.filter(
          (exercise) =>
            exercise.getName(t, language) === exerciseName &&
            exercise.workout &&
            exercise.workout.id !== targetWorkout.id &&
            // if no selectedWorkout.date, allow all;
            // otherwise require workout.date and that it’s before selectedWorkout.date
            (!selectedWorkoutDate ||
              (exercise.workout.date &&
                exercise.workout.date < selectedWorkoutDate))
        )
      )
    );

    // Sort exercises by workout date in descending order
    return sortedExercises.sort(
      (a, b) => new Date(b.workout.date) - new Date(a.workout.date)
    );
  }

  getTotalBlocksWithExercise(t, language, options = {}) {
    const targetWorkout =
      options?.selectedWorkout ?? this.selectedBlock?.selectedWorkout ?? null;
    const targetExercise =
      options?.exercise ??
      targetWorkout?.selectedExercise ??
      targetWorkout?.selectedExerciseToAdd ??
      this.selectedBlock?.selectedWorkout?.selectedExercise ??
      this.selectedBlock?.selectedWorkout?.selectedExerciseToAdd ??
      null;

    if (!targetWorkout || !targetExercise) {
      return 0;
    }

    const exerciseName = targetExercise.getName(t, language);
    const selectedWorkoutDate = targetWorkout?.date || new Date();

    // Filter blocks that contain the exercise in a workout before the selected workout date
    const blocksWithExercise = this.blocks.filter((block) =>
      block.workouts.some((workout) =>
        workout.exercises.some(
          (exercise) =>
            exercise.getName(t, language) === exerciseName &&
            exercise.workout &&
            exercise.workout.date && // Ensure workout has a date
            exercise.workout.date < selectedWorkoutDate // Only count past workouts
        )
      )
    );

    return blocksWithExercise.length;
  }
  // #endregion

  // #region /* BILLING */
  get lastFee() {
    if (this.feeHistory.length > 0) {
      return this.feeHistory.reduce((latestFee, currentFee) => {
        const latestFeeDate = new Date(
          latestFee.fee_start_date_year,
          latestFee.fee_start_date_month - 1,
          latestFee.fee_start_date_day
        );
        const currentFeeDate = new Date(
          currentFee.fee_start_date_year,
          currentFee.fee_start_date_month - 1,
          currentFee.fee_start_date_day
        );

        return currentFeeDate > latestFeeDate ? currentFee : latestFee;
      });
    } else {
      return null;
    }
  }

  get lastFeeEndDate() {
    if (this.lastFee) {
      const feeTypeIndex =
        this.lastFee.fee_type === "Trimestral"
          ? 3
          : this.lastFee.fee_type === "Semestral"
          ? 6
          : this.lastFee.fee_type === "Anual"
          ? 12
          : 1;
      const endMonthIndex = this.lastFee.fee_start_date_month + feeTypeIndex;

      const endMonthReturn =
        endMonthIndex > 12 ? endMonthIndex - 12 : endMonthIndex;

      const endYearReturn =
        endMonthIndex > 12
          ? this.lastFee.fee_start_date_year + 1
          : this.lastFee.fee_start_date_year;

      return `${this.lastFee.fee_start_date_day} / ${endMonthReturn} / ${endYearReturn}`;
    } else {
      return "-";
    }
  }

  lastFeeEndDateParts(part) {
    if (this.lastFee) {
      const feeTypeIndex =
        this.lastFee.fee_type === "Trimestral"
          ? 3
          : this.lastFee.fee_type === "Semestral"
          ? 6
          : this.lastFee.fee_type === "Anual"
          ? 12
          : 1;
      const endMonthIndex = this.lastFee.fee_start_date_month + feeTypeIndex;

      const endMonthReturn =
        endMonthIndex > 12 ? endMonthIndex - 12 : endMonthIndex;

      const endYearReturn =
        endMonthIndex > 12
          ? this.lastFee.fee_start_date_year + 1
          : this.lastFee.fee_start_date_year;

      if (part === "Month") {
        return endMonthReturn;
      } else {
        return endYearReturn;
      }
    } else {
      return "-";
    }
  }

  get owedQuotas() {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth() + 1; // Adjusted to 1-indexed months
    const currentDate = today.getDate();

    const lastFee = this.lastFee;
    if (!lastFee) {
      // No last fee found, assume no quotas are owed
      return 0;
    }

    let owedQuotas = 0;
    let endMonthIndex = lastFee.fee_start_date_month;
    let endYear = lastFee.fee_start_date_year;

    // Calculate intervals based on the fee type from the lastFee
    const intervalMonths =
      lastFee.fee_type === "Trimestral"
        ? 3
        : lastFee.fee_type === "Semestral"
        ? 6
        : lastFee.fee_type === "Anual"
        ? 12
        : 1;

    while (true) {
      // Calculate the next payment end month and year considering overflow to next year
      endMonthIndex += intervalMonths;
      if (endMonthIndex > 12) {
        endYear++;
        endMonthIndex -= 12;
      }

      // Stop the loop if the end date exceeds or equals today's date
      if (
        endYear > currentYear ||
        (endYear === currentYear && endMonthIndex > currentMonth) ||
        (endYear === currentYear &&
          endMonthIndex === currentMonth &&
          lastFee.fee_start_date_day >= currentDate)
      ) {
        break;
      }

      // Increment owedQuotas for each interval fully past
      owedQuotas++;
    }

    return owedQuotas;
  }

  owedAmount(t) {
    if (this.lastFee) {
      return this.owedQuotas > 0
        ? t("owedQuotasRow-string", {
            amount: this.owedQuotas * this.lastFee.fee,
            quotas: this.owedQuotas,
          })
        : t("feeWithCurrency-string", { fee: 0 });
    } else {
      return "-";
    }
  }

  get totalAmount() {
    return this.clientFee * 12;
  }

  get sortedFeeHistory() {
    // Clone the feeHistory array to avoid mutating the original array
    const sortedFees = [...this.feeHistory];

    // Sort the cloned array from newest to oldest based on start date
    sortedFees.sort((a, b) => {
      // Create date objects for each fee's start date
      const dateA = new Date(
        a.fee_start_date_year,
        a.fee_start_date_month - 1,
        a.fee_start_date_day
      );
      const dateB = new Date(
        b.fee_start_date_year,
        b.fee_start_date_month - 1,
        b.fee_start_date_day
      );

      // Compare the dates to sort in descending order (newest first)
      return dateB - dateA;
    });

    return sortedFees;
  }

  // #endregion

  // #region /* FORMS */
  hasForm(form) {
    //Deprecated
    // Mirar si volem que sigui nomes si ha respos o no (te alguna answer diferent a "")
    const hasForm = this.forms.some((f) => f.originalFormId === form.id);
    return hasForm;
  }

  numberOfForms(form) {
    if (this.hasForm(form)) {
      const sameForms = this.forms.filter((f) => f.originalFormId === form.id);
      return sameForms.length;
    } else {
      return 0;
    }
  }

  formTypePicker = "";

  searchTermClientForms = "";

  getFilteredClientForms() {
    const searchTermLower = this.searchTermClientForms.toLowerCase();

    // Filter by search term and form type
    const filteredForms = this.forms.filter((form) => {
      const matchesSearchTerm = form.name
        .toLowerCase()
        .includes(searchTermLower);
      const matchesFormType =
        !this.formTypePicker || form.type === this.formTypePicker;

      return matchesSearchTerm && matchesFormType && form.date !== null;
    });

    // Sort forms by date
    const sortedForms = filteredForms.sort((a, b) => b.date - a.date);

    return sortedForms;
  }

  get selectedForm() {
    const selectedForm = this.forms.find(
      (form) => form.isSelectedForAnswers === true
    );
    return selectedForm;
  }

  setSelectedForm(selectedForm) {
    this.forms.forEach((form) => {
      if (selectedForm && form.id === selectedForm.id) {
        form.setIsSelectedForAnswers(true);
      } else {
        form.setIsSelectedForAnswers(false);
      }
    });
  }

  async uploadForm(form) {
    let promises = [];
    // Wrap the transaction in a promise so it can be awaited
    const transactionPromise = runTransaction(db, async (transaction) => {
      // Upload or Update Forms in Firestore
      const formDocRef = doc(db, "users", this.id, "forms", form.id);

      transaction.set(formDocRef, form.uploadToCloud());

      const userDocRef = doc(db, "users", this.id);

      const data = {
        weekly_form_id: this.weeklyFormId,
        block_form_id: this.blockFormId,
      };
      transaction.update(userDocRef, data);
    });

    promises.push(transactionPromise);

    const results = await Promise.allSettled(promises);
    const hasError = results.some((result) => result.status === "rejected");
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        console.error(`Error in transaction ${index}:`, result.reason);
      }
    });
    if (hasError) {
      console.log("Error performing transaction for coach");
    } else {
    }
  }

  async uploadClientFormId(isWeekly) {
    let hasError = false;
    const userDocRef = doc(db, "users", this.id);

    // Prepare the data to update
    const data = isWeekly
      ? { weekly_form_id: this.weeklyFormId }
      : { block_form_id: this.blockFormId };

    try {
      // Correctly use await to handle the async update operation
      await updateDoc(userDocRef, data);
    } catch (error) {
      console.error("Error updating Firestore document: ", error);
      hasError = true;
    }
  }
  // #endregion

  // #region /* COMPETITIONS */

  addCompetition(competition, weightClass) {
    const newComp = new Competition(
      competition.id.toLowerCase(),
      competition.name,
      competition.date,
      null,
      this,
      weightClass,
      competition.competitionClass,
      competition.competitionLevel,
      competition.localization,
      competition.weightIn,
      competition.hour,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined
    );

    this.competitions.push(newComp);
  }

  initAddCompetitionView(competition) {
    if (competition) {
      const foundCompetition = this.competitions.filter(
        (c) => c.id === competition.id
      );
      if (foundCompetition[0]) {
        this.selectedWeightClass = foundCompetition[0].weightClass;
        this.coach.competitionSelectedClients.push(this);
      } else {
        this.selectedWeightClass = "";
      }
    } else {
      this.selectedWeightClass = "";
    }
  }

  resetAuxCompetition() {
    this.selectedWeightClass = "";
  }

  async addOrEditCompetition(competition) {
    const foundCompetition = this.competitions.filter(
      (c) => c.id === competition.id
    );
    const competitionSnapshotUpdate = buildClientCompetitionSnapshotUpdate({
      competition,
      selectedWeightClass: this.selectedWeightClass,
      clientGender: this.gender,
    });

    if (foundCompetition[0]) {
      foundCompetition[0].name = competitionSnapshotUpdate.name;
      foundCompetition[0].competitionClass =
        competitionSnapshotUpdate.competitionClass;
      foundCompetition[0].competitionLevel =
        competitionSnapshotUpdate.competitionLevel;
      foundCompetition[0].date = competitionSnapshotUpdate.date;
      foundCompetition[0].weightClass = competitionSnapshotUpdate.weightClass;

      this.updateClientTransactionCompetitions(competition.id);
    } else {
      console.log("PROVA COMPETIS CREATING A NEW FOR: ", this.nameForCoach);
      const newComp = new Competition(
        competition.id.toLowerCase(),
        competitionSnapshotUpdate.name,
        competitionSnapshotUpdate.date,
        null,
        this,
        competitionSnapshotUpdate.weightClass,
        competitionSnapshotUpdate.competitionClass,
        competitionSnapshotUpdate.competitionLevel,
        competition.localization,
        competition.weightIn,
        competition.hour,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined
      );

      console.log("PROVA COMPETIS CREATING: ", newComp);

      this.competitions.push(newComp);
      this.updateClientTransactionCompetitions(competition.id);
    }

    // Reset form fields and selections
    this.selectedWeightClass = "";

    this.startOrResetBatchTimer();
  }

  get selectedCompetition() {
    const selectedCompetition = this.competitions.find(
      (competition) => competition.isSelected === true
    );
    return selectedCompetition;
  }

  setSelectedCompetition(selectedCompetition) {
    this.competitions.forEach((competition) => {
      if (selectedCompetition && competition.id === selectedCompetition.id) {
        competition.setIsSelected(true);
      } else {
        competition.setIsSelected(false);
      }
    });
  }

  deleteCompetition(competition) {
    // this.updateClientDeletionCompetitions(competition.id);
    // const competitionToDelete = this.competitions.filter(
    //   (c) => c.id === competition.id
    // );
    // if (competitionToDelete) {
    // this.competitions = this.competitions.filter(
    //   (comp) => comp.id !== competition.id
    // );
    //   this.updateClientDeletionCompetitions(competition.id);
    //   this.startOrResetBatchTimer();
    // }
    const index = this.competitions.findIndex((c) => c.id === competition.id);
    if (index > -1) {
      if (!this.clientDeletionCompetitions.includes(competition.id)) {
        this.clientDeletionCompetitions.push(competition.id);
      }

      this.competitions.splice(index, 1);

      this.startOrResetBatchTimer();
    }
    this.selectedWeightClass = "";
  }
  setIsSelectedComp(value) {
    this.isSelectedComp = value;
  }
  // #endregion

  // #region /* ARRAYS */
  getFilteredData(exerciseName, t, language) {
    const exerciseGroupsAux = new Map();

    let names = [];
    let banList = [];
    let useSpecificProtocol = false;
    let slots = null;

    const basicExercise = exerciseName.replace(/-string$/, "");

    if (this.activeBlock()) {
      // Initialize protocols based on the exercise name
      const allExercises = this.activeBlock().getAllExercisesArray;
      const slotExercises = allExercises.filter(
        (ex) => ex.getProtocolBasicExercise === basicExercise
      );

      // Build an array of desired protocol title strings based on the basic exercise
      let desiredTitles = [];
      if (basicExercise === "squat") {
        desiredTitles = [
          ProtocolTitles.PRIMARY_SQUAT,
          ProtocolTitles.SECONDARY_SQUAT,
          ProtocolTitles.TERTIARY_SQUAT,
          ProtocolTitles.QUATERNARY_SQUAT,
          ProtocolTitles.QUINARY_SQUAT,
        ];
      } else if (basicExercise === "benchPress") {
        desiredTitles = [
          ProtocolTitles.PRIMARY_BENCH_PRESS,
          ProtocolTitles.SECONDARY_BENCH_PRESS,
          ProtocolTitles.TERTIARY_BENCH_PRESS,
          ProtocolTitles.QUATERNARY_BENCH_PRESS,
          ProtocolTitles.QUINARY_BENCH_PRESS,
        ];
      } else if (basicExercise === "deadlift") {
        desiredTitles = [
          ProtocolTitles.PRIMARY_DEADLIFT,
          ProtocolTitles.SECONDARY_DEADLIFT,
          ProtocolTitles.TERTIARY_DEADLIFT,
          ProtocolTitles.QUATERNARY_DEADLIFT,
          ProtocolTitles.QUINARY_DEADLIFT,
        ];
      }

      slots = slotExercises;

      // Check each protocol name and add it if it's not empty
      if (desiredTitles && slotExercises.length > 0) {
        useSpecificProtocol = true;

        desiredTitles.forEach((protocolTitle, slotIdx) => {
          const filteredExercises = slotExercises.filter(
            (e) => e.protocolSlot === protocolTitle
          );

          if (filteredExercises.length === 0) return; // skip empty

          if (!exerciseGroupsAux.has(slotIdx)) {
            exerciseGroupsAux.set(slotIdx, new Map());
          }
          const slotMap = exerciseGroupsAux.get(slotIdx);

          filteredExercises.forEach((exercise) => {
            const name = exercise.getName(t, language);
            if (!slotMap.has(name)) slotMap.set(name, []);
            const erm = exercise.getMaxErm;
            if (erm > 0) {
              slotMap.get(name).push({
                x: exercise.workout?.week ?? 1,
                y: erm,
              });
            }
          });

          slotMap.forEach((points) => {
            points.sort((a, b) => a.x - b.x);
          });
        });
      }
    }

    if (!useSpecificProtocol) {
      if (this.activeBlock() && this.activeBlock().workouts) {
        if (exerciseName === "squat-string") {
          names = ["squat", "sentadilla", "sq"];
          banList = [
            "sentadilla búlgara",
            "sentadilla bulgara",
            "bulgarian split squat",
            "split squat",
            "belt squat",
            "sentadilla primaria",
          ];
        } else if (exerciseName === "benchPress-string") {
          names = ["press banca", "bench press", "press de banca", "bp"];
          banList = [
            "press banca inclinado",
            "press banca declinado",
            "incline bench press",
            "decline bench press",
            "press banca primaria",
          ];
        } else if (exerciseName === "deadlift-string") {
          // Assumes deadlift by default
          names = ["deadlift", "peso muerto", "dl"];
          banList = [
            "peso muerto a una pierna",
            "peso muerto piernas rectas",
            "remo pendlay",
            "single leg rdl",
            "stiff leg rdl",
            "pendlay row",
            "peso muerto primario",
          ];
        }

        if (!exerciseGroupsAux.has(0)) {
          exerciseGroupsAux.set(0, new Map()); // Ensure index 0 exists
        }

        const allExercises = this.activeBlock().getAllExercisesArray;
        const slotMap = exerciseGroupsAux.get(0);

        allExercises.forEach((exercise) => {
          const exerciseName = exercise.getName(t, language);

          const nameMatches = names.some((name) =>
            exerciseName.toLowerCase().includes(name)
          );
          const nameNotBanned = banList.every(
            (banName) => !exerciseName.toLowerCase().includes(banName)
          );

          if (nameMatches && nameNotBanned && exercise.getMaxErm > 0) {
            if (!slotMap.has(exerciseName)) {
              slotMap.set(exerciseName, []); // Initialize an array for the exercise name
            }
            slotMap.get(exerciseName).push({
              x: exercise.workout.week ?? 1,
              y: exercise.getMaxErm,
            });
          }
        });
      }
    }

    return exerciseGroupsAux;
  }

  getFilteredDataTrac() {
    const datasets = {};

    const toCamelCase = (str) =>
      str.replace(/(_\w)/g, (matches) => matches[1].toUpperCase());

    const transformSorenessValue = (value) => (value === 0 ? 0 : 6 - value);

    Object.keys(this.coach.tracToShow).forEach((key) => {
      if (this.coach.tracToShow[key]) {
        datasets[key] = [];
      }
    });

    this.tracs.forEach((trac) => {
      const timestamp = new Date(trac.date).getTime();
      // Ensure timestamp is valid and within range
      if (
        !isNaN(timestamp) &&
        timestamp >= (this.activeBlockMinDate || 0) &&
        timestamp <= (this.activeBlockMaxDate || Infinity)
      ) {
        Object.keys(datasets).forEach((metric) => {
          const camelCaseMetric = toCamelCase(metric);
          let metricValue = Number(trac[camelCaseMetric]); // Convert to Number to handle potential non-numeric values gracefully
          // Apply transformation if applicable and ensure metricValue is a number and not Infinity or NaN
          if (
            metric.includes("soreness") &&
            !isNaN(metricValue) &&
            metricValue > 0 &&
            metricValue <= 5
          ) {
            metricValue = transformSorenessValue(metricValue);
          }
          // Add data point if value is valid
          if (
            typeof metricValue === "number" &&
            isFinite(metricValue) &&
            metricValue > 0
          ) {
            datasets[metric].push({ x: timestamp, y: metricValue });
          }
        });
      }
    });

    // Convert datasets to array format expected by Victory, filtering any empty datasets
    return Object.keys(datasets)
      .map((metric) => ({
        metric: metric,
        data: datasets[metric].filter((d) => isFinite(d.x) && isFinite(d.y)),
      }))
      .filter((dataset) => dataset.data.length > 0); // Further filter out any datasets without data
  }

  getFilteredDataBodyweight() {
    let filteredData = [];
    if (
      this.bodyweights &&
      this.activeBlockMinDate &&
      this.activeBlockMaxDate
    ) {
      filteredData = this.bodyweights
        .filter((bodyweight) => {
          const timestamp = new Date(bodyweight.date).getTime();
          const bodyweightValue = bodyweight.bodyweight;
          return (
            timestamp >= this.activeBlockMinDate &&
            timestamp <= this.activeBlockMaxDate &&
            !isNaN(bodyweightValue)
          );
        })
        .map((bodyweight) => {
          const timestamp = new Date(bodyweight.date).getTime();
          return {
            x: timestamp,
            y: bodyweight.bodyweight,
          };
        });
    }
    return filteredData;
  }

  // #endregion

  // #region /* CLIENT */
  setSettings(property, newValue) {
    this.clientSettings[property] = newValue;
  }

  saveClientProfile(clientData) {
    const userDocRef = doc(db, "users", this.id);
    const payload = { ...clientData };

    if (
      Object.prototype.hasOwnProperty.call(
        payload,
        "show_nutrition_plan_totals_override"
      ) &&
      payload.show_nutrition_plan_totals_override === null
    ) {
      payload.show_nutrition_plan_totals_override = deleteField();
    }

    // this.updateBatch(userDocRef, data);
    updateDoc(userDocRef, payload)
      .then(() => {
        console.log("Client Profile Saved");
      })
      .catch((error) => {
        console.error("Error updating or creating document:", error);
      });
  }

  emptyArrays() {
    this.clientDeletionWorkouts = [];
    this.clientTransactionData = [];
    this.clientDeletionData = [];
    this.clientTransactionNotes = [];
    this.clientDeletionNotes = [];
    this.clientTransactionCompetitions = [];
    this.clientDeletionCompetitions = [];
    this.clientTransactionForms = [];
    this.clientDeletionForms = [];
    this.clientTransactionNutritionPlans = [];
    this.clientDeletionNutritionPlans = [];
    this.clientTransactionProfile = [];
  }

  hasPendingQueuedWrites() {
    return (
      this.clientDeletionWorkouts.length > 0 ||
      this.clientTransactionData.length > 0 ||
      this.clientDeletionData.length > 0 ||
      this.clientTransactionNotes.length > 0 ||
      this.clientDeletionNotes.length > 0 ||
      this.clientTransactionCompetitions.length > 0 ||
      this.clientDeletionCompetitions.length > 0 ||
      this.clientTransactionForms.length > 0 ||
      this.clientDeletionForms.length > 0 ||
      this.clientTransactionNutritionPlans.length > 0 ||
      this.clientDeletionNutritionPlans.length > 0 ||
      this.clientTransactionProfile.length > 0 ||
      this.hasPendingChatOutboxMessages ||
      this.clientNutritionPlanStore?.hasUnsyncedLocalChanges === true
    );
  }

  async deleteClient() {
    if (this.coach && this.coach.clients) {
      this.coach.setProperty("selectedClientId", null);

      const userDocRef = doc(db, "users", this.id);

      // Update the user document by deleting specific fields
      try {
        // Delete each form in the client's local forms array directly
        if (this.forms && this.forms.length > 0) {
          this.forms.forEach((form) => {
            const formDocRef = doc(db, "users", this.id, "forms", form.id);
            deleteDoc(formDocRef).catch((error) => {
              console.error("Error deleting form document:", error);
            });
          });
        }

        //Delete each competition
        if (this.competitions && this.competitions.length > 0) {
          this.competitions.forEach((competition) => {
            const compDocRef = doc(
              db,
              "users",
              this.id,
              "competitions",
              competition.id
            );
            deleteDoc(compDocRef).catch((error) => {
              console.error("Error deleting competition document:", error);
            });
          });
        }

        await deleteActiveClientNutritionData({ clientId: this.id });

        await updateDoc(userDocRef, buildDeleteClientProfileCleanupPayload());

        await this.deleteAllChatData();
      } catch (error) {
        console.error("Error deleting coach_id:", error);
      }
    }
  }

  async deleteInvitation() {
    if (this.coach && this.coach.clients) {
      // Filter out the current client based on a unique identifier like userId
      // this.coach.clients = this.coach.clients.filter(
      //   (client) => client.id !== this.id
      // );
      console.log("deleteInvitation this.invitationId", this.invitationId);

      // Assuming setProperty is synchronous or doesn't return a Promise
      this.coach.setProperty("selectedClientId", null);

      const userInvitationRef = doc(db, "invitations", this.invitationId);

      try {
        await updateDoc(
          userInvitationRef,
          buildCoachCancelInvitationPatch({
            coachId: this.coach?.id,
            canceledAt: serverTimestamp(),
          })
        );
      } catch (error) {
        if (error?.code === "not-found") {
          console.warn(
            "Invitation was already removed externally; treating cancel as idempotent.",
            {
              invitationId: this.invitationId,
            }
          );
          return;
        }
        console.error("Error canceling invitation:", error);
        // Depending on your error handling strategy, you might want to re-throw the error or handle it differently
      }
    }
  }
  // #endregion

  // #region /* STATS */
  blocksToShowMetricsRow(t, language) {
    if (this.exercisesMetricsSelected.length === 0) {
      return this.sortedBlocks;
    }

    return this.sortedBlocks.filter((block) => {
      const blockExercises = block.getAllExercisesNames(t, language);
      return this.exercisesMetricsSelected.every((exercise) =>
        blockExercises.includes(exercise)
      );
    });
  }

  exercisesToShowMetricsRow(t, language) {
    const pinnedExercises = Array.isArray(this.pinnedExercises)
      ? this.pinnedExercises
      : [];
    const metricsPicked = Array.isArray(this.exercisesMetricsPicked)
      ? this.exercisesMetricsPicked
      : [];
    const selectedBlocks = Array.isArray(this.metricsSelectedBlocks)
      ? this.metricsSelectedBlocks
      : [];

    if (selectedBlocks.length === 0) {
      const uniqueMetricsPicked = metricsPicked.filter(
        (exercise) => !pinnedExercises.includes(exercise)
      );
      return [...pinnedExercises, ...uniqueMetricsPicked];
    }

    const blocksToShow =
      (this.blocksToShowMetricsRow &&
        this.blocksToShowMetricsRow(t, language)) ||
      [];

    // Build intersection safely
    const exerciseSets = selectedBlocks.map(
      (block) => new Set(block.getAllExercisesNames?.(t, language) ?? [])
    );
    const exercisesInAllBlocks =
      exerciseSets.length === 0
        ? new Set()
        : exerciseSets.reduce((common, set) => {
            return new Set([...common].filter((e) => set.has(e)));
          });

    const filteredExercisesInAllBlocks = [...exercisesInAllBlocks].filter(
      (exercise) =>
        blocksToShow.some((block) =>
          (block.getAllExercisesNames?.(t, language) ?? []).includes(exercise)
        )
    );

    const filteredPinnedExercises = pinnedExercises.filter((exercise) =>
      filteredExercisesInAllBlocks.includes(exercise)
    );

    const filteredMetricsPicked = metricsPicked.filter(
      (exercise) =>
        filteredExercisesInAllBlocks.includes(exercise) &&
        !filteredPinnedExercises.includes(exercise)
    );

    return [...filteredPinnedExercises, ...filteredMetricsPicked];
  }

  getFilteredDataStatistics(t, language) {
    //
    let structuredData = {};

    const exercisesToShow = this.exercisesToShowMetricsRow(t, language);

    const processExercise = (blockName, exercise, dateValue) => {
      if (!structuredData[blockName]) {
        structuredData[blockName] = {};
      }

      if (!structuredData[blockName][exercise.getName(t, language)]) {
        structuredData[blockName][exercise.getName(t, language)] = {};
      }

      this.selectedMetrics.forEach((metric) => {
        if (!structuredData[blockName][exercise.getName(t, language)][metric]) {
          structuredData[blockName][exercise.getName(t, language)][metric] = [];
        }

        let value = 0;
        if (metric === "e1rm-string") {
          value = exercise.getMaxErm > 0 ? exercise.getMaxErm : 0;
        } else if (metric === "tonnage-string") {
          value = exercise.getTonnage > 0 ? exercise.getTonnage : 0;
        } else if (metric === "Stress Index") {
          value = exercise.getStressIndex > 0 ? exercise.getStressIndex : 0;
        } else if (metric === "intensityPercentage-string") {
          value =
            exercise.getAverageIntensity > 0 ? exercise.getAverageIntensity : 0;
        } else if (metric === "NL") {
          value = exercise.getNumberLifts > 0 ? exercise.getNumberLifts : 0;
        } else if (metric === "NS") {
          value = exercise.getNumberSeries > 0 ? exercise.getNumberSeries : 0;
        } else if (metric === "XL") {
          value = exercise.getExertionLoad > 0 ? exercise.getExertionLoad : 0;
        }

        if (value > 0) {
          structuredData[blockName][exercise.getName(t, language)][metric].push(
            {
              x: dateValue,
              y: value,
              series: exercise.series.map((serie) => ({
                weight: serie.weight,
                rpe: serie.rpe,
                reps: serie.reps,
              })),
            }
          );
        }
      });
    };

    if (this.metricsSelectedBlocks && this.metricsSelectedBlocks.length > 0) {
      this.metricsSelectedBlocks.forEach((block) => {
        block.workouts.forEach((workout) => {
          workout.exercises.forEach((exercise) => {
            if (
              this.exercisesMetricsSelected.includes(
                exercise.getName(t, language)
              ) &&
              exercisesToShow.includes(exercise.getName(t, language)) &&
              exercise.workout &&
              exercise.workout.week >= this.startWeek &&
              exercise.workout.week <= this.endWeek
            ) {
              processExercise(block.name, exercise, workout.week);
            }
          });
        });
      });
    } else {
      const sortedExercises = this.blocks
        .map((block) => {
          return block.workouts
            .flatMap((workout) => workout.exercises)
            .filter((exercise) => {
              const exerciseDate = new Date(exercise.getDate);
              return (
                this.exercisesMetricsSelected.includes(
                  exercise.getName(t, language)
                ) &&
                exercisesToShow.includes(exercise.getName(t, language)) &&
                exercise.getDate &&
                exerciseDate >= this.metricsStartDate &&
                exerciseDate <= this.metricsEndDate
              );
            });
        })
        .flat()
        .sort((a, b) => new Date(b.getDate) - new Date(a.getDate));

      sortedExercises.forEach((exercise) => {
        processExercise(null, exercise, new Date(exercise.getDate).getTime());
      });
    }

    return structuredData;
  }

  searchTermExercisesMetrics = "";

  get getSearchTermExercisesMetrics() {
    return this.searchTermExercisesMetrics;
  }
  setSearchTermExercisesMetrics(w) {
    this.searchTermExercisesMetrics = w;
  }
  getFilteredExercisesMetrics(t, language) {
    const searchTermExercisesMetrics = this.searchTermExercisesMetrics
      ? this.searchTermExercisesMetrics.toLowerCase()
      : "";

    let allExercises = this.getAllExercises(t, language);
    const extraExercises = this.pinnedExercises;

    // Flatten extraExercises in case it is an array of arrays and merge unique exercises
    extraExercises.forEach((exercise) => {
      if (!allExercises.includes(exercise)) {
        allExercises.push(exercise);
      }
    });

    if (searchTermExercisesMetrics && allExercises.length > 0) {
      return allExercises.filter((ex) => {
        return ex.toLowerCase().includes(searchTermExercisesMetrics);
      });
    } else {
      return allExercises;
    }
  }

  // #endregion

  //  #region /* CALENDAR */
  get selectedMacroBlocks() {
    // 1) filter to only the selected macro
    const filtered = this.blocks.filter(
      (block) => block.label === this.selectedMacro
    );

    // 2) sort as desired
    const sorted = filtered.sort((a, b) => {
      const hasA = Boolean(a.initialDate);
      const hasB = Boolean(b.initialDate);

      if (!hasA && !hasB) return 0; // neither has date
      if (!hasA) return -1; // a missing → a first
      if (!hasB) return 1; // b missing → b first
      return (
        new Date(a.initialDate).getTime() - new Date(b.initialDate).getTime()
      );
    });

    // // 3) log to verify order
    // sorted.forEach((block) => {
    //   console.log(`Block: ${block.name}, initialDate: ${block.initialDate}`);
    // });

    // 4) return the sorted array
    return sorted;
  }

  getContiguousBlockIdsForSeed(seedBlock) {
    if (!seedBlock) return new Set();

    const label = seedBlock.label ?? "";

    // Local helpers to avoid external dependencies
    const mondayAtHour = (date, hour = 12) => {
      const d = new Date(date);
      const offset = (d.getDay() + 6) % 7; // Monday = 0
      d.setDate(d.getDate() - offset);
      d.setHours(hour, 0, 0, 0);
      return d;
    };

    const getInterval = (b) => {
      if (!b?.initialDate) return null;
      const start = mondayAtHour(b.initialDate, 12);
      const weeks = Math.max(
        0,
        Number(b.effectiveTotalWeeks ?? b.totalWeeks) || 0
      );
      const endExclusive = new Date(start);
      endExclusive.setDate(endExclusive.getDate() + weeks * 7);
      return { b, start, endExclusive };
    };

    const same = (this.blocks || []).filter(
      (b) => (b?.label ?? "") === label && b?.initialDate
    );
    const other = (this.blocks || []).filter(
      (b) => (b?.label ?? "") !== label && b?.initialDate
    );

    const sameIntervals = same
      .map(getInterval)
      .filter(Boolean)
      .sort((a, b) => a.start - b.start);

    const otherIntervals = other.map(getInterval).filter(Boolean);

    if (sameIntervals.length === 0) return new Set();

    const byId = new Map(sameIntervals.map((x) => [x.b.id, x]));
    const seed = byId.get(seedBlock.id);
    if (!seed) return new Set([seedBlock.id]);

    const clusterIds = new Set([seedBlock.id]);
    let left = new Date(seed.start);
    let right = new Date(seed.endExclusive);

    const hasOtherInGap = (from, to) => {
      if (+to <= +from) return false;
      for (const oi of otherIntervals) {
        if (+oi.endExclusive > +from && +oi.start < +to) return true; // overlaps the gap
      }
      return false;
    };

    let extended = true;
    while (extended) {
      extended = false;

      // --- Extend backward: overlap with current left
      for (const si of sameIntervals) {
        if (clusterIds.has(si.b.id)) continue;
        if (si.start < left && si.endExclusive > left) {
          clusterIds.add(si.b.id);
          left = new Date(Math.min(+left, +si.start));
          extended = true;
        }
      }
      if (extended) continue;

      // --- Extend backward: nearest earlier block (touching or with empty gap)
      let prev = null;
      for (const si of sameIntervals) {
        if (clusterIds.has(si.b.id)) continue;
        if (+si.endExclusive <= +left) {
          if (!prev || +si.endExclusive > +prev.endExclusive) prev = si; // closest on the left
        }
      }
      if (prev && !hasOtherInGap(prev.endExclusive, left)) {
        clusterIds.add(prev.b.id);
        left = new Date(Math.min(+left, +prev.start));
        extended = true;
        continue;
      }

      // --- Extend forward: overlap with current right
      for (const si of sameIntervals) {
        if (clusterIds.has(si.b.id)) continue;
        if (si.start < right && si.endExclusive > right) {
          clusterIds.add(si.b.id);
          right = new Date(Math.max(+right, +si.endExclusive));
          extended = true;
        }
      }
      if (extended) continue;

      // --- Extend forward: nearest later block (touching or with empty gap)
      let next = null;
      for (const si of sameIntervals) {
        if (clusterIds.has(si.b.id)) continue;
        if (+si.start >= +right) {
          if (!next || +si.start < +next.start) next = si; // closest on the right
        }
      }
      if (next && !hasOtherInGap(right, next.start)) {
        clusterIds.add(next.b.id);
        right = new Date(Math.max(+right, +next.endExclusive));
        extended = true;
        continue;
      }
    }

    return clusterIds;
  }

  get labelBaseColorMap() {
    const labels = Array.from(
      new Set((this.blocks || []).map((b) => this.normalizeLabel(b?.label)))
    );

    // stable order across years/views
    labels.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );

    const map = Object.create(null);
    labels.forEach((label, i) => {
      map[label] = Calendar.BASE_COLORS[i % Calendar.BASE_COLORS.length];
    });
    return map;
  }

  get macroColorMap() {
    const out = Object.create(null);
    Object.entries(this.labelBaseColorMap).forEach(([label, hex]) => {
      out[label] = toRgba(hex, Calendar.MAX_OPACITY);
    });
    return out;
  }

  get blocksByLabelSorted() {
    const by = Object.create(null);
    (this.blocks || []).forEach((b) => {
      const label = this.normalizeLabel(b?.label);
      if (!Object.prototype.hasOwnProperty.call(by, label)) {
        by[label] = [];
      }
      by[label].push(b);
    });
    Object.values(by).forEach((arr) =>
      arr.sort((a, b) => {
        const da = a?.initialDate
          ? new Date(a.initialDate).getTime()
          : Number.POSITIVE_INFINITY;
        const db = b?.initialDate
          ? new Date(b.initialDate).getTime()
          : Number.POSITIVE_INFINITY;
        return da - db;
      })
    );
    return by;
  }

  get blockColorMapById() {
    const map = Object.create(null);
    const by = this.blocksByLabelSorted;
    Object.entries(by).forEach(([label, blocks]) => {
      const hex = this.labelBaseColorMap[label];
      const total = blocks.length || 1;
      blocks.forEach((blk, idx) => {
        map[blk.id] = toRgba(hex, ((idx + 1) * Calendar.MAX_OPACITY) / total);
      });
    });
    return map;
  }
  normalizeLabel(s) {
    return (s ?? "").trim();
  }

  getLabelColor(label) {
    const hex = this.labelBaseColorMap[this.normalizeLabel(label)];
    return hex ? toRgba(hex, Calendar.MAX_OPACITY) : "transparent";
  }
  getBlockColor(blockOrId) {
    const id = typeof blockOrId === "string" ? blockOrId : blockOrId?.id;
    return this.blockColorMapById[id] ?? "transparent";
  }

  // #endregion

  // #region /* HELPERS */
  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }

  get showToast() {
    return this.coach.showToast;
  }

  get rirOrRpe() {
    return this.coach.rirOrRpe ?? false;
  }

  get isOffline() {
    return this.coach ? this.coach.isOffline : true;
  }

  get hasPartialFetchedBlocksInMemory() {
    return (
      this.wasPartiallyEvicted === true &&
      Array.isArray(this.blocks) &&
      this.blocks.length > 0
    );
  }

  get canQueueBlockTransactions() {
    return this.isFetched || this.hasPartialFetchedBlocksInMemory;
  }

  get isOfflineSelectable() {
    return this.isOffline
      ? this.canQueueBlockTransactions || this.hasBeenFetchedOnce
      : true;
  }

  get isAvailable() {
    return this.isOffline ? this.canQueueBlockTransactions : true;
  }

  updateClientTransactionData(id) {
    if (!this.clientTransactionData.includes(id)) {
      this.clientTransactionData.push(id);
    }
  }

  updateClientDeletionWorkouts(id) {
    if (!this.clientDeletionWorkouts.includes(id)) {
      this.clientDeletionWorkouts.push(id);
    }
  }

  updateClientTransactionNotes(id) {
    if (!this.clientTransactionNotes.includes(id)) {
      this.clientTransactionNotes.push(id);
    }
  }
  updateClientDeletionNotes(id) {
    if (!this.clientDeletionNotes.includes(id)) {
      this.clientDeletionNotes.push(id);
    }
  }

  updateClientTransactionCompetitions(id) {
    if (!this.clientTransactionCompetitions.includes(id)) {
      this.clientTransactionCompetitions.push(id);
    }
  }
  updateClientDeletionCompetitions(id) {
    console.log("updateClientDeletionCompetitions 1");
    if (!this.clientDeletionCompetitions.includes(id)) {
      console.log("updateClientDeletionCompetitions 2");
      this.clientDeletionCompetitions.push(id);
    }
  }

  updateClientTransactionForms(id) {
    if (!this.clientTransactionForms.includes(id)) {
      this.clientTransactionForms.push(id);
    }
  }
  updateClientDeletionForms(id) {
    if (!this.clientDeletionForms.includes(id)) {
      this.clientDeletionForms.push(id);
    }
  }

  updateClientTransactionNutritionPlans(id) {
    return undefined;
  }

  updateClientDeletionNutritionPlans(id) {
    return undefined;
  }

  updateClientTransactionProfile(id) {
    if (!this.clientTransactionProfile.includes(id)) {
      this.clientTransactionProfile.push(id);
    }
  }

  // #endregion

  // #region /* DEMO CLIENT */
  async createDemoClientData() {
    const exercisePlan = [
      {
        workouts: [
          [
            {
              name: "400 Tempo Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
                {
                  name: "400Tempo-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.PRIMARY_SQUAT,
              protocolText: [
                "Ramp 3x3 @6 + x3 @4",
                "Ramp 3x3 @6.5 + x3 @4.5",
                "Ramp 3x3 @7 + x3 @5",
                "Ramp 3x3 @7 + x3 @5.5",
                "Ramp 3x3 @7.5 + x3 @5.5",
                "Ramp 3x3 @7.5 + x3 @6",
                "Ramp 3x3 @8 + x3 @6",
              ],
              series: [
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4, 4.5, 5, 5, 5.5, 5.5, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [170, 175, 180, 185, 190, 195, 200],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 5.5, 6, 6, 6.5, 6.5, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [180, 185, 190, 195, 200, 205, 210],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [6, 6.5, 7, 7, 7.5, 7.5, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [190, 195, 200, 205, 210, 215, 220],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [170, 175, 180, 185, 190, 195, 200],
                },
              ],
            },
            {
              name: "2ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "2ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.PRIMARY_BENCH_PRESS,
              protocolText: [
                "x1 @5.5 + x4 @6.5 + 3x4 @5",
                "x1 @5.5 + x4 @6.5 + 3x4 @5",
                "x1 @6 + x4 @6.5 + 3x4 @5",
                "x1 @6.5 + x4 @7 + 3x4 @6",
                "x1 @6.5 + x4 @7.5 + 3x4 @6",
                "x1 @7 + x4 @8 + 3x4 @6",
                "x1 @8 + x4 @8.5 + 3x4 @6",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5.5, 5.5, 6, 6.5, 6.5, 7, 8],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [152.5, 155, 157.5, 160, 162.5, 162.5, 167.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6.5, 6.5, 6.5, 7, 7.5, 8, 8.5],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [145, 147.5, 150, 152.5, 152.5, 155, 157.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 145, 147.5, 150, 150, 150],
                },
              ],
            },
            {
              name: "Romanian Deadlift",
              originalName: "romanianDeadlift-string",
              series: [
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [4, 4, 4, 4, 4, 4, 4],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [185, 185, 185, 190, 190, 190, 195],
                },
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [5, 5, 5, 5, 5, 5, 5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [195, 195, 195, 200, 200, 200, 205],
                },
                {
                  targetReps: [6, 6, 6, 6, 6, 6, 6],
                  targetRPE: [6, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [205, 205, 205, 210, 210, 210, 215],
                },
              ],
            },
            {
              name: "Machine Press",
              originalName: "machinePress-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 62.5, 65, 65, 65],
                },
              ],
            },
            {
              name: "Lateral Raise",
              originalName: "lateralRaise-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [12, 12, 12, 12, 12, 12, 12],
                },
              ],
            },
          ],

          [
            {
              name: "1ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "1ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.TERTIARY_BENCH_PRESS,
              protocolText: [
                "x5 @5 + 2x5 @6",
                "x5 @5 + 2x5 @6",
                "x5 @5 + 2x5 @6",
                "3x5 @6.5",
                "3x5 @6.5",
                "3x5 @6.5",
                "3x5 @6.5",
              ],
              series: [
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [5, 5, 5, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6, 6, 6.5, 6.5, 6.5, 6.5],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [135, 137.5, 140, 142.5, 145, 145, 145],
                },
              ],
            },
            {
              name: "Sumo Deadlift",
              originalName: "sumoDeadlift-string",
              protocolSlot: ProtocolTitles.PRIMARY_DEADLIFT,
              protocolText: [
                "Ramp 3x5 @6",
                "Ramp 3x5 @6.5",
                "Ramp 3x5 @7",
                "Ramp 3x5 @7.5",
                "Ramp 3x5 @7.5",
                "Ramp 3x5 @8",
                "Ramp 3x5 @8",
              ],
              series: [
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [210, 215, 220, 225, 230, 235, 240],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [220, 225, 230, 235, 240, 245, 250],
                },
                {
                  targetReps: [5, 5, 5, 5, 5, 5, 5],
                  targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [230, 235, 240, 245, 250, 255, 260],
                },
              ],
            },
            {
              name: "Dumbbell Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "Dumbbell-string",
                  title: "equipment-string",
                  subtitle: "",
                },
                ,
              ],
              series: [
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [8, 8, 8, 8, 8, 8, 8],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [38, 40, 40, 42, 42, 44, 44],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [8, 8, 8, 8, 8, 8, 8],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [38, 40, 40, 42, 42, 44, 44],
                },
              ],
            },
            {
              name: "Leg Curls",
              originalName: "legCurls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [80, 80, 80, 80, 85, 85, 85],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [80, 80, 80, 80, 85, 85, 85],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [0, 0, 0, 0, 0, 0, 0],
                },
              ],
            },
            {
              name: "Pull Down",
              originalName: "pullDown-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 65, 65, 65, 70, 70],
                },
              ],
            },
            {
              name: "Cable Row",
              originalName: "cableRow-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 60, 65, 65, 65],
                },
              ],
            },
            {
              name: "Tricep Pushdown",
              originalName: "tricepPushdown-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [32.5, 35, 35, 35, 35, 37.5, 37.5],
                },
              ],
            },
            {
              name: "Curls",
              originalName: "curls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 18, 18],
                },
              ],
            },
          ],

          [
            {
              name: "Low Bar Squat",
              originalName: "squat-string",
              attributes: [
                {
                  name: "LowBar-string",
                  title: "stanceGrip-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.SECONDARY_SQUAT,
              protocolText: [
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @5 + x4 @6 + 2x4 @5",
                "x4 @6 + x4 @7 + 2x4 @6",
                "x4 @6 + x4 @7 + 2x4 @6",
                "x4 @6 + x4 @7 + 2x4 @6",
              ],
              series: [
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [190, 192.5, 195, 197.5, 200, 202.5, 202.5],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6, 6, 6, 6, 7, 7, 7],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [195, 197.5, 200, 200, 202.5, 205, 210],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5, 5, 5, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 182.5, 185, 187.5, 190, 190, 190],
                },
              ],
            },
            {
              name: "3ct Pause Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "3ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.QUATERNARY_BENCH_PRESS,
              protocolText: [
                "3x1 @5",
                "3x1 @5.5",
                "3x1 @6",
                "3x1 @6.5",
                "3x1 @6.5",
                "3x1 @7",
                "3x1 @7",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [145, 147.5, 150, 150, 152.5, 155, 155],
                },
              ],
            },
            ,
            {
              name: "Bulgarian Split Squat",
              originalName: "bulgarianSplitSquat-string",
              series: [
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
                {
                  targetReps: [8, 8, 8, 8, 8, 8, 8],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [36, 36, 36, 38, 38, 38, 40],
                },
              ],
            },
            {
              name: "Leg Extension",
              originalName: "legExtension-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [85, 85, 85, 90, 90, 90, 90],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [85, 85, 85, 90, 90, 90, 90],
                },
              ],
            },
          ],

          [
            {
              name: "2ct Pause Spoto Bench Press",
              originalName: "benchPress-string",
              attributes: [
                {
                  name: "2ctPause-string",
                  title: "tempo-string",
                  subtitle: "",
                },
                {
                  name: "Spoto-string",
                  title: "rom-string",
                  subtitle: "",
                },
              ],
              protocolSlot: ProtocolTitles.SECONDARY_BENCH_PRESS,
              protocolText: [
                "2x1 @6 + 3x3 @5",
                "2x1 @7 + 3x3 @6",
                "2x1 @7 + 3x3 @6",
                "2x1 @7.5 + 3x3 @6",
                "2x1 @7.5 + 3x3 @6",
                "2x1 @8 + 3x3 @6",
                "2x1 @8 + 3x3 @6",
              ],
              series: [
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                },
                {
                  targetReps: [1, 1, 1, 1, 1, 1, 1],
                  targetRPE: [6, 7, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [147.5, 150, 150, 152.5, 155, 157.5, 160],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
                {
                  targetReps: [3, 3, 3, 3, 3, 3, 3],
                  targetRPE: [5, 6, 6, 6, 6, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [140, 142.5, 142.5, 145, 147.5, 150, 150],
                },
              ],
            },
            {
              name: "Conventional Deadlift",
              originalName: "conventionalDeadlift-string",
              protocolSlot: ProtocolTitles.SECONDARY_DEADLIFT,
              protocolText: [
                "Ramp 3x4 @6",
                "Ramp 3x4 @6.5",
                "Ramp 3x4 @7",
                "Ramp 3x4 @7.5",
                "Ramp 3x4 @7.5",
                "Ramp 3x4 @8",
                "Ramp 3x4 @8",
              ],
              series: [
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [4, 4.5, 5, 5.5, 5.5, 6, 6],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [180, 185, 187.5, 190, 195, 200, 200],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [5, 5.5, 6, 6.5, 6.5, 7, 7],
                  relevance: [2, 2, 2, 2, 2, 2, 2],
                  weight: [190, 195, 197.5, 200, 205, 210, 210],
                },
                {
                  targetReps: [4, 4, 4, 4, 4, 4, 4],
                  targetRPE: [6, 6.5, 7, 7.5, 7.5, 8, 8],
                  relevance: [1, 1, 1, 1, 1, 1, 1],
                  weight: [200, 205, 207.5, 210, 215, 220, 220],
                },
              ],
            },
            {
              name: "Cable Single Arm Pull Down",
              originalName: "pullDown-string",
              attributes: [
                {
                  name: "Cable-string",
                  title: "equipment-string",
                  subtitle: "",
                },
                {
                  name: "SingleArm-string",
                  title: "other-string",
                  subtitle: "",
                },
              ],
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [60, 60, 60, 65, 65, 65, 65],
                },
              ],
            },
            {
              name: "Row",
              originalName: "row-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [70, 70, 70, 75, 75, 75, 75],
                },
              ],
            },
            {
              name: "Tricep Extensions",
              originalName: "tricepExtensions-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [35, 35, 35, 35, 35, 35, 35],
                },
              ],
            },
            {
              name: "Curls",
              originalName: "curls-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [16, 16, 16, 16, 16, 16, 18],
                },
              ],
            },
            {
              name: "Lateral Raise",
              originalName: "lateralRaise-string",
              series: [
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
                {
                  targetReps: [10, 10, 10, 10, 10, 10, 10],
                  targetRPE: [9, 9, 9, 9, 9, 9, 9],
                  relevance: [3, 3, 3, 3, 3, 3, 3],
                  weight: [14, 14, 14, 14, 14, 14, 16],
                },
              ],
            },
          ],
        ],
      },
      // Repeat this structure for weeks 2 to 7
    ];

    const randNum = Math.floor(1000 + Math.random() * 9000);

    const newBlockId = String(uuidv4());

    const newBlock = new Block(
      newBlockId,
      "Develop Block",
      "", // notes
      [], // workouts
      true,
      true,
      this,
      6,
      6,
      ""
    );

    // Helper function to get a random date between two dates
    const getRandomDate = (startDate, endDate) => {
      const date = new Date(
        startDate.getTime() +
          Math.random() * (endDate.getTime() - startDate.getTime())
      );
      return new Date(date.setHours(0, 0, 0, 0)); // Ensure the time is set to midnight
    };

    // Function to generate unique random dates
    const generateUniqueRandomDates = (startDate, endDate, numDates) => {
      const dates = new Set();
      while (dates.size < numDates) {
        dates.add(
          getRandomDate(startDate, endDate).toISOString().split("T")[0]
        );
      }
      return Array.from(dates).map((date) => new Date(date));
    };

    // Function to generate random bodyweights with unique dates
    const generateRandomBodyweights = (
      numBodyweights,
      startWeight,
      endWeight,
      startDate,
      endDate
    ) => {
      const bodyweights = [];
      const dates = generateUniqueRandomDates(
        startDate,
        endDate,
        numBodyweights
      );

      let currentWeight = startWeight;
      const totalWeightChange = endWeight - startWeight;
      const averageDailyChange = totalWeightChange / numBodyweights;

      dates.forEach((date) => {
        const randomFluctuation = Math.random() * 0.2 - 0.1; // Random change between -0.1 and +0.1
        const dailyChange = averageDailyChange + randomFluctuation;
        currentWeight += dailyChange;
        bodyweights.push(new Bodyweight(undefined, currentWeight, date, this));
      });

      return bodyweights;
    };

    // Function to generate random Trac entries with unique dates
    const generateRandomTracs = (numTracs, startDate, endDate, client) => {
      const tracs = [];
      const dates = generateUniqueRandomDates(startDate, endDate, numTracs);

      dates.forEach((date) => {
        const legSoreness = Math.floor(Math.random() * 3) + 3;
        const pushSoreness = Math.floor(Math.random() * 3) + 3;
        const pullSoreness = Math.floor(Math.random() * 2) + 2;
        const motivation = Math.floor(Math.random() * 5) + 1;
        const perceivedRecovery = Math.floor(Math.random() * 3) + 2;
        const sleepQualityNutrition = Math.floor(Math.random() * 2) + 4;
        const technicalComfort = Math.floor(Math.random() * 3) + 3;

        tracs.push(
          new Trac(
            undefined,
            date,
            legSoreness,
            pushSoreness,
            pullSoreness,
            motivation,
            perceivedRecovery,
            sleepQualityNutrition,
            technicalComfort,
            client
          )
        );
      });

      return tracs;
    };

    // Helper function to get the date of a specific weekday `weeksAgo` weeks ago
    const getDateOfSpecificWeekday = (weeksAgo, weekday) => {
      const today = new Date(); // Get today's date
      const currentDay = today.getDay(); // Get the current day of the week (0 = Sunday, 1 = Monday, etc.)
      const startOfWeek = new Date(today); // Create a new date object for the start of the week

      // Calculate the start of the week (Monday)
      startOfWeek.setDate(today.getDate() - currentDay + 1);
      if (currentDay === 0) {
        startOfWeek.setDate(startOfWeek.getDate() - 7); // Adjust if today is Sunday
      }

      // Calculate the date `weeksAgo` weeks ago
      startOfWeek.setDate(startOfWeek.getDate() - weeksAgo * 7);

      // Calculate the target date
      const targetDate = new Date(startOfWeek);
      targetDate.setDate(startOfWeek.getDate() + (weekday - 1));

      return targetDate;
    };

    // Helper function to adjust date by weeks and days
    const adjustDateByWeeksAndDays = (startDate, weeks, days) => {
      const adjustedDate = new Date(startDate);
      adjustedDate.setDate(startDate.getDate() - weeks * 7 + days);
      return adjustedDate;
    };

    // Weekdays for Monday, Wednesday, Thursday, and Saturday
    const weekdays = [1, 3, 4, 6]; // 1 = Monday, 3 = Wednesday, 4 = Thursday, 6 = Saturday

    // Generate the workout dates
    let firstWorkoutDate = null;
    let lastWorkoutDate = null;

    // Create the workouts for the 7-week training block
    for (let week = 5; week >= 0; week--) {
      for (let day = 0; day < 4; day++) {
        const startDate = getDateOfSpecificWeekday(6 - week, weekdays[day]);

        if (!firstWorkoutDate || startDate < firstWorkoutDate) {
          firstWorkoutDate = startDate;
        }
        if (!lastWorkoutDate || startDate > lastWorkoutDate) {
          lastWorkoutDate = startDate;
        }

        const newWorkout = new Workout(
          undefined,
          startDate,
          this.coach?.coachLanguage === "en"
            ? `Day ${day + 1}`
            : `Día ${day + 1}`,
          day + 1, // order
          week + 1, // week
          false,
          newBlock,
          []
        );

        exercisePlan[0].workouts[day].forEach((exerciseInfo, index) => {
          const {
            originalName,
            name,
            attributes,
            series,
            protocolSlot,
            protocolText,
          } = exerciseInfo;
          const newExercise = new Exercise(
            undefined,
            name,
            originalName,
            "", // notes
            index, // order
            undefined,
            index === 0,
            newWorkout,
            [],
            []
          );
          if (protocolSlot) {
            newExercise.protocolSlot = protocolSlot;
          }
          if (protocolText?.length > 0) {
            newExercise.protocolText = protocolText[week];
          }
          if (series) {
            // Add sets/series
            for (let setIndex = 0; setIndex < series.length; setIndex++) {
              const serieInfo = series[setIndex];
              if (serieInfo) {
                const newSerie = new Serie(
                  undefined,
                  setIndex + 1, // order
                  serieInfo.targetReps[week],
                  serieInfo.targetRPE[week], // Specific targetRPE for this week
                  serieInfo.weight[week], // Specific weight for this week
                  serieInfo.targetReps[week],
                  serieInfo.targetRPE[week], // target_rpe
                  0, // target_weight
                  newExercise,
                  serieInfo.relevance[week],
                  undefined,
                  serieInfo.relevance[week] === 1
                );
                newExercise.series.push(newSerie);
              }
            }
          }
          // Add attributes if present
          if (attributes) {
            attributes.forEach((attribute, attrIndex) => {
              const newAttribute = new Attribute(
                String(uuidv4()),
                attribute.name,
                attribute.title,
                attribute.subtitle,
                attrIndex, // order
                false, // isPredefined
                false,
                null, // date
                newExercise
              );
              newExercise.attributes.push(newAttribute);
            });
          }

          newWorkout.exercises.push(newExercise);
        });

        newBlock.workouts.push(newWorkout);
      }
    }
    // Generate bodyweights and tracs within the workout period
    const numBodyweights = 20; // Specify the number of bodyweights
    const numTracs = 20; // Specify the number of tracs

    const bodyweights = generateRandomBodyweights(
      numBodyweights,
      74.8,
      74,
      firstWorkoutDate,
      lastWorkoutDate
    );
    const tracs = generateRandomTracs(
      numTracs,
      firstWorkoutDate,
      lastWorkoutDate,
      this
    );
    this.bodyweights.push(...bodyweights);
    this.tracs.push(...tracs);

    this.blocks.push(newBlock);
    this.setProperty("startWeek", 1);
    this.setProperty("endWeek", 6);

    const clientArraysDocRef = doc(db, "users", this.id, "arrays", "arrays");

    const formattedTracs = this.tracs.map((trac) => trac.uploadToCloud());
    const formattedBodyweights = this.bodyweights.map((bodyweight) =>
      bodyweight.uploadToCloud()
    );

    await setDoc(clientArraysDocRef, {
      attributes: [],
      predefined_exercises: [],
      tracs: formattedTracs,
      bodyweights: formattedBodyweights,
      notes: [],
    });
    await commitProgramBlockWrite({
      pathSegments: ["users", this.id, "blocks", newBlockId],
      block: newBlock,
      allWorkoutsTouched: true,
      timestamp: NetworkManager.getInstance().getNetworkTime(),
      context: {
        owner: "client_demo_program",
        clientId: this.id,
        blockId: newBlockId,
      },
    });
  }
  // #endregion

  // #region /* NUTRITION */

  get effectiveShowNutritionPlanTotals() {
    if (this.showNutritionPlanTotalsOverride !== null) {
      return this.showNutritionPlanTotalsOverride;
    }

    return this.coach?.defaultShowNutritionPlanTotals ?? true;
  }

  initAuxNutritionPlan(originalPlan) {
    this.auxNutritionPlan = null;
    return null;
  }

  nutritionPlanInWeekday(targetWeekday) {
    const weekdaysMap = {
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
      sunday: 7,
    };
    const dayNum = weekdaysMap[targetWeekday.replace("-string", "")];
    if (!dayNum) {
      console.warn(`Invalid weekday: ${targetWeekday}`);
      return [];
    }

    return this.nutritionPlans.find((np) => np.weekDay === targetWeekday);
  }

  copyNutritionPlan(t, nutritionPlan) {
    return null;
  }

  async copyNutritionPlanToClient(t, nutritionPlan) {
    return null;
  }

  // #endregion
}
